import { useState, useRef } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';

const geoUrl = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

const stateDocs = {
  Florida: [
    {
      title: 'Florida Tax Deed Law Reference Guide',
      url: 'https://docs.google.com/document/d/1_9OWeS2O3-EpZ8bFZAedmLg1wE-0SQHV/edit?usp=sharing',
      type: 'Legal Reference',
    },
  ],
  Texas: [
    {
      title: 'Texas Tax Deed Law Reference Guide',
      url: 'https://docs.google.com/document/d/1Yzx5VUFNFeg3SV55WOaGzwfKBWbru7IY/edit?usp=sharing',
      type: 'Legal Reference',
    },
  ],
};

const stateData = [
  {
    state: 'Georgia',
    type: 'Tax Deed',
    redeemableDeed: true,
    penalty: '20% flat penalty',
    redemption: '12 months',
    schedule: 'First Tuesday monthly',
    minReturn: '20%',
    notes: 'Fixed by state law (OCGA 48-4-42). Avoid homestead properties - 2 year redemption.',
    rating: 'A',
    details: {
      overview: 'Georgia is one of the best tax deed states for investors. The 20% penalty is fixed by state law and cannot be bid down.',
      keyRules: [
        'OCGA 48-4-42 sets a flat 20% penalty on the tax amount',
        'Redemption period is 12 months from sale date',
        'Homestead properties have a 2-year redemption - avoid these',
        'Auctions held first Tuesday of every month at the courthouse',
        'No quiet title needed if held past redemption period',
      ],
      targetCounties: [
        { county: 'Gwinnett', notes: 'Suburban Atlanta, strong demand, competitive but worth it' },
        { county: 'Hall', notes: 'Gainesville area, growing market, less competition' },
        { county: 'Cherokee', notes: 'North Atlanta suburbs, high ARVs' },
        { county: 'Forsyth', notes: 'Fast growing, strong rental demand' },
      ],
      apnSearch: [
        { label: 'Gwinnett County Property Search', url: 'https://www.gwinnettassessor.manatron.com' },
        { label: 'Hall County Property Search', url: 'https://www.hallcounty.org' },
        { label: 'Georgia Superior Court Clerks', url: 'https://www.gsccca.org' },
      ],
      auctionLinks: [
        { label: 'GA SRI Tax Sales', url: 'https://www.sri-taxsale.com/Tax/Georgia' },
      ],
      warnings: [
        'Homestead exemption means 2 year redemption not 12 months',
        'Always check for IRS liens - survive the sale with 120-day redemption',
        'Verify property is not in a flood zone',
      ],
    },
  },
  {
    state: 'Florida',
    type: 'Tax Deed',
    penalty: 'N/A - true deed',
    redemption: 'None',
    schedule: 'Weekly Thursdays',
    minReturn: '20%',
    notes: 'No redemption period. Quiet title needed before selling. Avoid Miami-Dade and Broward.',
    rating: 'A',
    details: {
      overview: 'Florida is a true tax deed state with no redemption period after the sale. You get immediate ownership but need quiet title before selling to buyers using financing.',
      keyRules: [
        'No redemption period - deed is final after sale',
        'Quiet title required before selling with financing ($1,500-$3,500)',
        'Auctions held weekly on Thursdays in most counties',
        'Online bidding via county-specific RealAuction portals',
        'Opening bid includes back taxes, interest, and fees',
      ],
      targetCounties: [
        { county: 'Hillsborough', notes: 'Tampa area, weekly auctions, strong rental market' },
        { county: 'Polk', notes: 'Lakeland area, lower prices, good cash flow' },
        { county: 'Pasco', notes: 'Growing market, less competition than Tampa proper' },
        { county: 'Volusia', notes: 'Daytona area, vacation rental potential' },
      ],
      apnSearch: [
        { label: 'Hillsborough Property Appraiser', url: 'https://www.hcpafl.org' },
        { label: 'Polk County Property Appraiser', url: 'https://www.polkpa.org' },
        { label: 'Florida Dept of Revenue', url: 'https://floridarevenue.com/property' },
      ],
      auctionLinks: [
        { label: 'Hillsborough Tax Deed Auctions', url: 'https://hillsborough.realtaxdeed.com' },
        { label: 'RealAuction Florida', url: 'https://www.realauction.com' },
      ],
      warnings: [
        'Avoid Miami-Dade and Broward - institutional investors dominate',
        'Quiet title adds 2-4 months before you can sell with financing',
        'HOA liens may survive - verify before bidding',
      ],
    },
  },
  {
    state: 'Texas',
    type: 'Tax Deed',
    redeemableDeed: true,
    penalty: '25% penalty',
    redemption: '180 days (non-homestead)',
    schedule: 'First Tuesday monthly',
    minReturn: '20%',
    notes: 'Focus on non-homestead. Homestead = 2 year redemption. Register 1 week prior in Bexar County.',
    rating: 'B',
    details: {
      overview: 'Texas offers strong returns but has a split redemption system. Non-homestead properties have 180-day redemption while homestead properties have 2 years.',
      keyRules: [
        'Non-homestead redemption: 180 days',
        'Homestead redemption: 2 years - avoid',
        'Auctions held first Tuesday of every month at courthouse',
        'Must register 1 week prior in most counties',
        '25% penalty on the tax amount if redeemed',
      ],
      targetCounties: [
        { county: 'Bexar', notes: 'San Antonio area, register 1 week early, strong market' },
        { county: 'Tarrant', notes: 'Fort Worth area, diverse inventory' },
        { county: 'Travis', notes: 'Austin area, high ARVs but competitive' },
        { county: 'El Paso', notes: 'Less competition, lower price points' },
      ],
      apnSearch: [
        { label: 'Bexar County Appraisal District', url: 'https://www.bcad.org' },
        { label: 'Tarrant County Appraisal District', url: 'https://www.tad.org' },
        { label: 'Travis County Appraisal District', url: 'https://www.traviscad.org' },
      ],
      auctionLinks: [
        { label: 'Texas Tax Sale Lists', url: 'https://www.tax-sale.info/texas' },
        { label: 'Bexar County Sheriff Sales', url: 'https://www.bexar.org/sheriff' },
      ],
      warnings: [
        'Always verify homestead vs non-homestead before bidding',
        'IRS liens survive with 120-day federal redemption right',
        'Some counties require in-person registration weeks in advance',
      ],
    },
  },
  {
    state: 'Florida',
    type: 'Tax Lien',
    penalty: '5% minimum floor',
    redemption: 'Varies',
    schedule: 'Annual',
    minReturn: '14%',
    notes: 'Mandatory 5% minimum even if bid to zero. Avoid Miami-Dade and Broward - too competitive.',
    rating: 'A',
    details: {
      overview: 'Florida tax lien certificates are one of the safest lien investments. State law guarantees a minimum 5% return even if you bid the interest rate to 0%.',
      keyRules: [
        'Bid down the interest rate - lowest bidder wins',
        'Minimum 5% penalty guaranteed by Florida law even at 0% bid',
        'Certificates earn 18% if not redeemed within 2 years',
        'After 2 years you can apply for tax deed',
        'Annual auctions typically held May-June',
      ],
      targetCounties: [
        { county: 'Polk', notes: 'Lower competition, good redemption rates' },
        { county: 'Volusia', notes: 'Daytona area, mixed portfolio' },
        { county: 'Marion', notes: 'Ocala area, rural but low competition' },
        { county: 'Pasco', notes: 'Growing area, solid returns' },
      ],
      apnSearch: [
        { label: 'Polk County Tax Collector', url: 'https://www.polktaxes.com' },
        { label: 'Florida Dept of Revenue', url: 'https://floridarevenue.com' },
      ],
      auctionLinks: [
        { label: 'LienHub Florida', url: 'https://www.lienhub.com' },
        { label: 'RealAuction Tax Liens', url: 'https://www.realauction.com' },
      ],
      warnings: [
        'Avoid Miami-Dade and Broward - institutional bidders drive rates to 0.25%',
        'Check property condition before bidding - you may end up owning it',
        'Verify no environmental issues on the parcel',
      ],
    },
  },
  {
    state: 'Maryland',
    type: 'Tax Lien',
    penalty: 'Varies by county',
    redemption: '6 months',
    schedule: 'Annual',
    minReturn: '14%',
    notes: 'Fastest redemption in US. Target Baltimore City, Prince Georges, Anne Arundel.',
    rating: 'A',
    details: {
      overview: 'Maryland has the shortest redemption period in the US at 6 months, meaning faster returns. Bid4Assets handles most Maryland counties making registration simple.',
      keyRules: [
        'Shortest redemption period in the US - 6 months',
        'Interest rates vary by county (typically 6-24%)',
        'Bid4Assets platform for most counties',
        'Single account works across all counties',
        'Annual auctions typically held in May',
        'If a lien goes unredeemed and the certificate holder completes foreclosure, a deed is issued at that point - Maryland itself does not run a separate, standalone deed auction',
      ],
      targetCounties: [
        { county: 'Baltimore City', notes: 'High volume, urban properties, strong returns' },
        { county: 'Prince Georges', notes: 'DC suburbs, high redemption rates' },
        { county: 'Anne Arundel', notes: 'Annapolis area, quality properties' },
        { county: 'Montgomery', notes: 'High income area, almost always redeems' },
      ],
      apnSearch: [
        { label: 'Maryland Property Search', url: 'https://sdat.dat.maryland.gov/RealProperty' },
        { label: 'Prince Georges County', url: 'https://www.princegeorgescountymd.gov' },
      ],
      auctionLinks: [
        { label: 'Bid4Assets Maryland', url: 'https://www.bid4assets.com/maryland' },
        { label: 'Baltimore City Tax Sale', url: 'https://www.baltimoremd.gov' },
      ],
      warnings: [
        'Research property condition - some Baltimore City properties are heavily distressed',
        'Environmental liens can attach and survive',
        'Always verify the property has value before bidding',
      ],
    },
  },
  {
    state: 'Indiana',
    type: 'Tax Lien',
    penalty: '15% on tax amount',
    redemption: '1 year',
    schedule: 'Annual (fall)',
    minReturn: '14%',
    notes: 'Low institutional competition. Target Marion, Allen, Hamilton counties.',
    rating: 'B',
    details: {
      overview: 'Indiana is an underrated tax lien state with low institutional competition. The 15% penalty on the tax amount makes it accessible for smaller investors.',
      keyRules: [
        '15% penalty on the delinquent tax amount',
        '1 year redemption period',
        'Annual auctions held in the fall (October-November)',
        'Low competition compared to Florida and Maryland',
        'After redemption period, certificate holder can apply for tax deed - Indiana does not run a separate, standalone deed auction',
      ],
      targetCounties: [
        { county: 'Marion', notes: 'Indianapolis, largest market, most inventory' },
        { county: 'Allen', notes: 'Fort Wayne area, affordable properties' },
        { county: 'Hamilton', notes: 'Indy suburbs, higher quality properties' },
        { county: 'Lake', notes: 'Gary area - higher risk but higher volume' },
      ],
      apnSearch: [
        { label: 'Marion County Assessor', url: 'https://www.assessor.org' },
        { label: 'Indiana Gateway Property', url: 'https://gateway.ifionline.org' },
      ],
      auctionLinks: [
        { label: 'SRI Indiana Tax Sales', url: 'https://www.sri-taxsale.com/Tax/Indiana' },
        { label: 'Bid4Assets Indiana', url: 'https://www.bid4assets.com/indiana' },
      ],
      warnings: [
        'Gary (Lake County) has significant blight and vacancy - research carefully',
        'Some properties may have environmental issues',
        'Verify clear title before bidding on high value properties',
      ],
    },
  },
  {
    state: 'California',
    type: 'Tax Deed',
    penalty: 'N/A - true deed',
    redemption: 'None',
    schedule: 'Varies by county (often spring/early summer)',
    minReturn: '20%',
    notes: 'No redemption after sale, but title insurance is hard to get for 1-5 years. GovEase and Bid4Assets both used.',
    rating: 'A',
    details: {
      overview: 'California is a true tax deed state - properties go to auction after 5 years of delinquency, with no redemption period once sold. Most counties use GovEase or Bid4Assets for online bidding, and inventory is concentrated in Southern California and the Central Valley.',
      keyRules: [
        'Properties become eligible for sale after 5 years of tax delinquency',
        'No redemption period after the sale closes',
        'Owner can redeem any time before the auction by paying taxes in full',
        'Most counties run online auctions via GovEase or Bid4Assets',
        'Title insurance is difficult to obtain for 1-5 years after the sale',
        'Many counties hold a "re-offer" sale later in the year for unsold parcels',
      ],
      targetCounties: [
        { county: 'Los Angeles', notes: 'Largest inventory in the state, uses GovEase, very competitive' },
        { county: 'San Diego', notes: 'Coastal and inland mix, annual auction' },
        { county: 'Riverside', notes: 'Inland Empire, lower price points, uses Bid4Assets' },
        { county: 'San Joaquin', notes: 'Central Valley, lower competition, affordable inventory' },
      ],
      apnSearch: [
        { label: 'LA County Assessor Property Search', url: 'https://portal.assessor.lacounty.gov' },
        { label: 'San Diego County Assessor', url: 'https://arcc.sdcounty.ca.gov' },
        { label: 'California State Controller - Public Auctions', url: 'https://www.sco.ca.gov/ardtax_public_auction.html' },
      ],
      auctionLinks: [
        { label: 'GovEase California Auctions', url: 'https://www.govease.com' },
        { label: 'Bid4Assets County Tax Sales', url: 'https://www.bid4assets.com/county-tax-sales' },
      ],
      warnings: [
        'Title insurance may be unavailable for 1-5 years - plan to hold or wholesale to cash buyers',
        'No physical inspection access before bidding - research from public records only',
        'High-value coastal counties (LA, San Diego, Orange) draw heavy institutional competition',
        'Confirm minimum bid covers all back taxes, penalties, and county fees before bidding',
      ],
    },
  },
  {
    state: 'Arizona',
    type: 'Tax Lien',
    penalty: '16% max, bid down',
    redemption: '3 years',
    schedule: 'Annual (February, Maricopa)',
    minReturn: '8-16%',
    notes: 'Bid down interest rate from 16%. Maricopa is huge but competitive; Pima tends to hold higher rates.',
    rating: 'A',
    details: {
      overview: 'Arizona is a mature, well-organized tax lien state. Interest starts at 16% and is bid down in 1% increments, with the lowest bidder winning the certificate. Maricopa County (Phoenix) is the largest market in the state by far, while Pima County (Tucson) tends to hold higher rates due to less institutional competition.',
      keyRules: [
        'Bidding starts at 16% annual interest and is bid down in 1% increments',
        'Lowest bid wins the certificate; a 0% bid is allowed',
        'Redemption period is 3 years from the date of sale',
        'Maricopa County auction held annually in February, online',
        'Some counties use RealAuction; others run their own county portal',
        'After the redemption period, certificate holders can pursue a treasurer\'s deed via court action - Arizona does not run a separate, standalone deed auction',
      ],
      targetCounties: [
        { county: 'Maricopa', notes: 'Phoenix metro, tens of thousands of certificates annually, but residential rates often bid down to 1-5%' },
        { county: 'Pima', notes: 'Tucson area, less competition, residential rates often 8-14%' },
        { county: 'Pinal', notes: 'Phoenix exurbs, growing, moderate competition' },
        { county: 'Yavapai', notes: 'Prescott area, smaller market, less institutional presence' },
      ],
      apnSearch: [
        { label: 'Maricopa County Treasurer', url: 'https://treasurer.maricopa.gov' },
        { label: 'Pima County Treasurer', url: 'https://www.to.pima.gov' },
        { label: 'Arizona Dept of Revenue - Property Tax', url: 'https://azdor.gov/businesses-arizona/property-tax' },
      ],
      auctionLinks: [
        { label: 'Maricopa County Tax Lien Sale', url: 'https://maricopa.arizonataxsale.com' },
        { label: 'Pima County RealAuction', url: 'https://www.realauction.com' },
      ],
      warnings: [
        'Maricopa residential liens are heavily bid down by institutional investors - focus on vacant land or commercial parcels for better rates',
        'A foreclosure (treasurer\'s deed) action requires going to court after the 3-year redemption - factor in legal costs',
        'Verify which platform a given county uses each year before registering, as this can change',
      ],
    },
  },
  {
    state: 'Nevada',
    type: 'Tax Deed',
    penalty: 'N/A - true deed',
    redemption: '2 years',
    schedule: 'Annual (varies by county, often spring)',
    minReturn: '20%',
    notes: 'Most NV counties sell deeds, not liens. Clark County (Las Vegas) is the dominant market.',
    rating: 'B',
    details: {
      overview: 'Nevada is primarily a tax deed state. Delinquent property is held by the county treasurer for a 2-year redemption period, and if not redeemed, it goes to public auction for the highest bid. A handful of marketing sources claim some counties sell tax lien certificates, but this is not consistent or well documented statewide - always confirm directly with the county treasurer rather than assuming lien availability.',
      keyRules: [
        '2-year redemption period held by the county treasurer before a sale can occur',
        'After redemption expires, the county board orders a public auction sale',
        'Sale goes to the highest bidder, with a minimum bid covering taxes, penalties, costs, and interest',
        'No further redemption period once the auction sale is complete',
        'Clark County (Las Vegas) represents roughly three-quarters of the state population and the bulk of inventory',
      ],
      targetCounties: [
        { county: 'Clark', notes: 'Las Vegas metro, largest inventory and population by far' },
        { county: 'Washoe', notes: 'Reno area, second largest market' },
        { county: 'Lyon', notes: 'Smaller market near Carson City, less competition' },
        { county: 'Douglas', notes: 'Lake Tahoe area, smaller but higher-value inventory' },
      ],
      apnSearch: [
        { label: 'Clark County Assessor', url: 'https://www.clarkcountynv.gov/government/elected_officials/assessor' },
        { label: 'Washoe County Assessor', url: 'https://www.washoecounty.gov/assessor' },
      ],
      auctionLinks: [
        { label: 'Clark County Treasurer Tax Sales', url: 'https://www.clarkcountynv.gov/government/elected_officials/treasurer' },
        { label: 'Nevada Tax Deed Sale Lists', url: 'https://www.taxtitleservices.com/nevada-tax-deed-sales-all-counties' },
      ],
      warnings: [
        'Do not assume tax lien certificates are available - verify directly with the county treasurer each year',
        'Auction schedules and minimum bids vary significantly by county - check each county individually',
        'Rural Nevada parcels can be remote with limited access - verify the property is usable before bidding',
      ],
    },
  },
  {
    state: 'Michigan',
    type: 'Tax Deed',
    penalty: 'N/A - true deed',
    redemption: 'None (forfeiture process ends ownership)',
    schedule: 'Annual, county-run (often fall)',
    minReturn: '20%',
    notes: 'Properties forfeit after 2 years delinquent and go through judicial foreclosure. Wayne County (Detroit) has the largest inventory.',
    rating: 'B',
    details: {
      overview: 'Michigan properties are forfeited to the county after 2 years of tax delinquency, then go through a judicial foreclosure process before being sold at auction with no redemption period for the buyer. Wayne County, which includes Detroit, has by far the largest volume of any county in the state.',
      keyRules: [
        'Delinquent taxes lead to forfeiture after 2 years (reduced from 5 years by a 1999 law change)',
        'A circuit court enters a Judgment of Foreclosure before the property can be sold',
        'No redemption period for the winning bidder once the sale is confirmed',
        'Wayne County typically holds its main auction in the fall (September and later)',
        'Many auctions run online; some smaller counties still use in-person sales',
      ],
      targetCounties: [
        { county: 'Wayne (Detroit)', notes: 'Largest inventory by far, heavy concentration of vacant lots and distressed homes, very price-sensitive market' },
        { county: 'Genesee (Flint)', notes: 'Significant blight, low price points, requires careful condition research' },
        { county: 'Oakland', notes: 'Detroit suburbs, higher quality inventory, more competition' },
        { county: 'Kent (Grand Rapids)', notes: 'Stronger West Michigan market, less distressed inventory' },
      ],
      apnSearch: [
        { label: 'Wayne County Treasurer', url: 'https://waynecountytreasurermi.com' },
        { label: 'Michigan Property Tax Info', url: 'https://www.michigan.gov/taxes' },
      ],
      auctionLinks: [
        { label: 'Wayne County Tax Foreclosure Auction', url: 'https://waynecountytreasurermi.com' },
        { label: 'Tax Title Services Michigan Lists', url: 'https://www.taxtitleservices.com' },
      ],
      warnings: [
        'A third of Detroit properties have gone through tax foreclosure since 2008 - inspect condition carefully before bidding',
        'Many Wayne County parcels are vacant lots, not structures - confirm exactly what you are bidding on',
        'Confirm whether the property is occupied; Michigan law has specific protections and timelines for occupied homes',
      ],
    },
  },
  {
    state: 'Pennsylvania',
    type: 'Tax Deed',
    penalty: 'N/A - true deed (Upset Sale) or clear title (Judicial Sale)',
    redemption: '9 months (owner-occupied, most counties)',
    schedule: 'Varies by county - Upset Sale typically annual, Judicial Sale follows',
    minReturn: '20%',
    notes: 'Unique 3-stage process: Upset Sale, then Judicial Sale, then Repository. Philadelphia and Allegheny County run their own separate systems.',
    rating: 'B',
    details: {
      overview: 'Pennsylvania uses a distinctive 3-stage tax sale process found in most of the 67 counties. The Upset Sale comes first and buyers take the property subject to existing liens. Anything unsold goes to a Judicial Sale, which wipes out most liens and gives clean title. Philadelphia and Allegheny County are exceptions and run their own systems entirely.',
      keyRules: [
        'Upset Sale: minimum bid covers back taxes and costs, but buyer takes property subject to existing liens and mortgages',
        'Judicial Sale: for properties unsold at Upset Sale, sold free and clear of most liens with proper notice to lienholders',
        'Repository Sale: open bids accepted any time for properties unsold at both prior sales',
        'Most counties give owner-occupied properties a 9-month right of redemption after a tax sale',
        'Philadelphia runs virtual auctions via Bid4Assets; Allegheny County skips the Upset Sale and uses Sheriff Sales instead',
      ],
      targetCounties: [
        { county: 'Philadelphia', notes: 'Runs its own Sheriff Sale system via Bid4Assets, 9-month redemption applies' },
        { county: 'Allegheny (Pittsburgh)', notes: 'No Upset Sale - goes straight to Sheriff Sale; city of Pittsburgh also runs a separate Treasurer Sale' },
        { county: 'Bucks', notes: 'Standard Upset/Judicial process, sold via GovDeals' },
        { county: 'Dauphin (Harrisburg)', notes: 'Standard Tax Claim Bureau process' },
      ],
      apnSearch: [
        { label: 'Philadelphia Sheriff Office', url: 'https://phillysheriff.com' },
        { label: 'PA Dept of Revenue', url: 'https://www.revenue.pa.gov' },
      ],
      auctionLinks: [
        { label: 'Bucks County Upset Sale (GovDeals)', url: 'https://www.govdeals.com' },
        { label: 'Philadelphia Sheriff Sales (Bid4Assets)', url: 'https://www.bid4assets.com' },
      ],
      warnings: [
        'Buying at an Upset Sale means you may inherit existing mortgages and liens - know the difference before bidding',
        'Philadelphia and Allegheny County do not follow the standard process - research each separately',
        'The 9-month redemption window means you cannot safely resell or renovate immediately after winning',
      ],
    },
  },
  {
    state: 'North Carolina',
    type: 'Tax Deed',
    penalty: 'N/A - true deed',
    redemption: 'None (10-day upset bid period only)',
    schedule: 'Rolling - no fixed annual date, varies by court docket',
    minReturn: '20%',
    notes: 'No tax lien certificates sold. Auctions happen as foreclosure cases finish court, not on a fixed calendar. 10-day upset bid period after each sale.',
    rating: 'B',
    details: {
      overview: 'North Carolina does not sell tax lien certificates - delinquent properties go through a court foreclosure process and are sold at the courthouse once a judgment is entered. There is no fixed annual sale date; auctions happen on a rolling basis as individual cases complete. Any winning bid is subject to a 10-day upset bid period.',
      keyRules: [
        'No lien certificates - North Carolina is deed-only',
        'Sales happen on a rolling basis as foreclosure suits complete, not on one set date per year',
        'After the high bid, a 10-day upset bid period allows anyone to top the bid by at least 5% (minimum $750)',
        'Each new upset bid restarts the 10-day clock',
        'No post-sale redemption once the upset bid period closes - bidder must be present in person in most counties',
      ],
      targetCounties: [
        { county: 'Mecklenburg (Charlotte)', notes: 'Largest county, in-person bidding only, no online option' },
        { county: 'Wake (Raleigh)', notes: 'Second largest, sales at the courthouse, same upset bid rules' },
        { county: 'Guilford (Greensboro)', notes: 'Mid-size market, less competition than Charlotte/Raleigh' },
        { county: 'Forsyth (Winston-Salem)', notes: 'Moderate competition, solid rental demand' },
      ],
      apnSearch: [
        { label: 'Mecklenburg County Tax Office', url: 'https://tax.mecknc.gov' },
        { label: 'Wake County Tax Administration', url: 'https://www.wake.gov' },
      ],
      auctionLinks: [
        { label: 'Mecklenburg County In Rem Foreclosures', url: 'https://tax.mecknc.gov/service/rem-foreclosures' },
        { label: 'Wake County Foreclosures', url: 'https://www.wake.gov/departments-government/tax-administration/real-estate/foreclosures' },
      ],
      warnings: [
        'There is no statewide auction calendar - you must track each county court docket individually',
        'In-person bidding is required in most counties - no online bidding option exists for these sales',
        'The 10-day upset bid period means your winning bid is not final until that window passes with no challenge',
      ],
    },
  },
  {
    state: 'South Carolina',
    type: 'Tax Lien',
    penalty: '3-12% scaled by quarter',
    redemption: '1 year',
    schedule: 'Annual (October-November)',
    minReturn: '12%',
    notes: 'Bid down interest from 12% max. Short 1-year redemption. Upcountry counties (Spartanburg, Cherokee) less competitive than coastal markets.',
    rating: 'A',
    details: {
      overview: 'South Carolina is a true tax lien state with a statutory maximum interest rate of 12%, bid down competitively. Its 1-year redemption period is among the shortest of any lien state, meaning faster turnaround for investors than many other states. Competition is heavy in coastal and Midlands markets but lighter in the upcountry.',
      keyRules: [
        'Statutory maximum interest is 12% per year, bid down - lowest bidder wins the certificate',
        'Redemption period is 1 year from the date of the tax sale',
        'Interest due to the investor scales up quarterly: 3% in the first quarter, up to the full 12% in the final quarter before redemption expires',
        'If not redeemed within the year, the certificate holder can apply for the tax deed - South Carolina does not run a separate, standalone deed auction',
        'Annual sales typically held October-November and run through competitive bidding',
      ],
      targetCounties: [
        { county: 'Spartanburg', notes: 'Upcountry, lower competition, bids often near the full 12%' },
        { county: 'Cherokee', notes: 'Upcountry, manufacturing-driven economy, stable property values' },
        { county: 'Greenville', notes: 'Larger urban market, more competitive bidding' },
        { county: 'Charleston', notes: 'Coastal, highest competition, rates often bid down significantly' },
      ],
      apnSearch: [
        { label: 'SC Association of Counties', url: 'https://www.sccounties.org' },
        { label: 'Spartanburg County Tax Collector', url: 'https://www.spartanburgcounty.org' },
      ],
      auctionLinks: [
        { label: 'Lancaster County Tax Sale Info', url: 'https://www.lancastercountysc.gov' },
        { label: 'Colleton County Tax Sale Info', url: 'https://www.colletoncounty.org' },
      ],
      warnings: [
        'Coastal and Midlands counties (Charleston, Greenville, Myrtle Beach) get bid down hard by competition',
        'Owners stay in possession during the redemption year - you have no right to enter or contact them',
        'Mobile/manufactured homes have extra rules including required rent payments at redemption',
      ],
    },
  },
  {
    state: 'Tennessee',
    type: 'Tax Deed',
    redeemableDeed: true,
    penalty: '~10% (redeemable deed)',
    redemption: '90 days to 1 year (scales with delinquency length)',
    schedule: 'Sporadic - varies by county court schedule',
    minReturn: '10%',
    notes: 'No fixed sale calendar - tracks each county court docket. Shelby County (Memphis) moved to online auctions via SRI in 2024.',
    rating: 'B',
    details: {
      overview: 'Tennessee is a redeemable tax deed state with roughly a 10% return for the investor if the property is redeemed. Unlike states with one fixed annual sale date, Tennessee auctions happen sporadically based on each county Chancery Court docket - some counties hold one sale a year, others two or more.',
      keyRules: [
        'Interest/penalty due to the purchaser is approximately 10% if the owner redeems',
        'Redemption period scales with how long the property was delinquent: 1 year if delinquent 5 years or less, 180 days if 5-8 years, 90 days if 8+ years',
        'Sales are confirmed by Chancery Court order before the buyer gets full rights',
        'Shelby County (Memphis) moved its tax sales online via SRI ZeusAuction.com starting April 2024',
        'No fixed statewide calendar - each county schedules sales as its foreclosure docket allows',
      ],
      targetCounties: [
        { county: 'Shelby (Memphis)', notes: 'Largest county, now online via SRI, typically 2 sales per year' },
        { county: 'Davidson (Nashville)', notes: 'Second largest, Chancery Court Clerk and Master runs sales' },
        { county: 'Knox (Knoxville)', notes: 'East Tennessee market, less competition than Memphis/Nashville' },
        { county: 'Hamilton (Chattanooga)', notes: 'Moderate market, worth tracking for less competitive deals' },
      ],
      apnSearch: [
        { label: 'Shelby County Trustee', url: 'https://www.shelbycountytrustee.com' },
        { label: 'Davidson County Chancery Clerk', url: 'https://chanceryclerkandmaster.nashville.gov' },
      ],
      auctionLinks: [
        { label: 'Shelby County Online Tax Sales (ZeusAuction)', url: 'https://www.zeusauction.com' },
        { label: 'Shelby County Tax Sale Info', url: 'https://www.shelbycountytn.gov' },
      ],
      warnings: [
        'There is no single statewide tax sale date - you must track each county court calendar separately',
        'Sale is "subject to right of redemption" - you do not get full, final ownership until that window closes',
        'IRS liens carry a separate 120-day federal redemption right that can extend beyond the state period',
      ],
    },
  },
  {
    state: 'Washington',
    type: 'Tax Deed',
    penalty: 'N/A - true deed',
    redemption: 'None for most owners (3 years only for minors/incompetents)',
    schedule: 'Annual, county-run (often fall)',
    minReturn: '20%',
    notes: 'Properties become eligible after 3 years delinquent. No standard redemption period. King County uses Bid4Assets.',
    rating: 'B',
    details: {
      overview: 'Washington is a true tax deed state. Properties become eligible for foreclosure after 3 years of delinquency, and once sold at auction, prior owners generally have no further rights to the property - one of the cleanest deed processes among the states covered here.',
      keyRules: [
        'Properties become subject to foreclosure once 3 years tax-delinquent',
        'No standard redemption period after the sale for most owners',
        'A narrow 3-year redemption right exists only for minors or legally incompetent former owners',
        'King County and many others use Bid4Assets for online auctions',
        'All sales are final - no guarantee that prior liens are fully extinguished, so title research matters',
      ],
      targetCounties: [
        { county: 'King (Seattle)', notes: 'Largest market, long-running Bid4Assets partnership, strong property values' },
        { county: 'Pierce (Tacoma)', notes: 'More affordable than King, decent inventory' },
        { county: 'Spokane', notes: 'Eastern Washington, lower competition than the Seattle metro' },
        { county: 'Snohomish', notes: 'Seattle exurbs, growing market' },
      ],
      apnSearch: [
        { label: 'King County Treasury', url: 'https://kingcounty.gov/en/dept/executive-services/buildings-property/treasury-operations' },
        { label: 'Washington Dept of Revenue - Property Tax', url: 'https://dor.wa.gov/taxes-rates/property-tax' },
      ],
      auctionLinks: [
        { label: 'King County Tax Foreclosure Auctions (Bid4Assets)', url: 'https://www.bid4assets.com/king' },
      ],
      warnings: [
        'No redemption period means the sale is essentially final for typical owners - do due diligence before bidding, not after',
        'Parcels can be redeemed by the owner any time up to the day before the auction, so the list can shrink right up to sale day',
        'King County and others make no guarantee that prior liens are extinguished - get legal advice on title before bidding',
      ],
    },
  },
  {
    state: 'Utah',
    type: 'Tax Deed',
    penalty: 'N/A - true deed',
    redemption: 'None (must redeem before the sale starts)',
    schedule: 'Annual, statewide in May',
    minReturn: '20%',
    notes: 'All counties hold sales in May with no post-sale redemption. Some use Bid Up format, others Undivided Interest (ownership percentage bid down).',
    rating: 'B',
    details: {
      overview: 'Utah holds tax sales annually in May across all counties, with property owners able to redeem only up until the moment the auction starts - once bidding opens, there is no redemption right at all. Some counties use a standard highest-bidder format while others use an unusual "Undivided Interest" format where the bid-down is on ownership percentage rather than price.',
      keyRules: [
        'Sales are held annually in May, often around or before Memorial Day',
        'Utah law allows zero redemption period once the auction begins - last chance to redeem is right before bidding opens',
        'Some counties (like Utah County) use both Bid Up (highest price) and Undivided Interest (lowest ownership percentage) formats depending on the parcel',
        'Owners, lienholders, or their associates cannot bid on their own property to effectively redeem it through a sale - this is treated as a redemption, not a new sale',
        'Opening bids include taxes, penalties, interest, and administrative/auction fees',
      ],
      targetCounties: [
        { county: 'Salt Lake', notes: 'Largest county, most inventory, online and in-person options' },
        { county: 'Utah (Provo)', notes: 'Third Thursday of May typically, online via PublicSurplus.com' },
        { county: 'Davis', notes: 'Salt Lake suburbs, smaller but steady inventory' },
        { county: 'Weber (Ogden)', notes: 'Northern Utah, less competitive than Salt Lake/Utah County' },
      ],
      apnSearch: [
        { label: 'Salt Lake County Auditor', url: 'https://www.saltlakecounty.gov/property-tax' },
        { label: 'Utah County Auditor', url: 'https://auditor.utahcounty.gov' },
      ],
      auctionLinks: [
        { label: 'Utah County May Tax Sale', url: 'https://auditor.utahcounty.gov/may-tax-sale' },
        { label: 'Salt Lake County Tax Sale', url: 'https://www.saltlakecounty.gov/property-tax/property-tax-sale' },
      ],
      warnings: [
        'There is zero redemption period once bidding opens - this is stricter than almost any other state covered here',
        'Undivided Interest sales mean you may only win a percentage of ownership, not the full property - read each parcel listing carefully',
        'Properties can be redeemed right up to the morning of the sale, so confirm the final list before bidding',
      ],
    },
  },
  {
    state: 'Ohio',
    type: 'Tax Deed',
    penalty: 'N/A - true deed (Sheriff Sale)',
    redemption: 'Until court confirms the sale',
    schedule: 'Varies by county, often year-round',
    minReturn: '20%',
    notes: 'About 33 of Ohio\'s 88 counties hold deed-style Sheriff Sales instead of lien sales. Premium and bulk bid methods used.',
    rating: 'B',
    details: {
      overview: 'Ohio is a hybrid state - roughly two-thirds of its 88 counties run deed-style "Sheriff Sale" foreclosure auctions, while the rest sell tax lien certificates (see the separate Tax Lien card for Ohio). In deed counties, the court orders the home sold at auction after a foreclosure case, and the previous owner can redeem only until the court formally confirms the sale.',
      keyRules: [
        'About 34 of Ohio\'s 88 counties sell tax lien certificates; the remainder run deed-style Sheriff Sales',
        'In deed counties, a court enters judgment and orders the home sold at a Sheriff Sale',
        'The prior owner can still redeem up until the court confirms the sale - after confirmation, ownership transfers permanently',
        'Auctions are typically held year-round rather than on one fixed date',
        'Premium bid and bulk bid methods are both used depending on the county',
      ],
      targetCounties: [
        { county: 'Lucas (Toledo)', notes: 'Runs weekly Sheriff Sale auctions, deed-style' },
        { county: 'Summit (Akron)', notes: 'Deed-style Sheriff Sales, moderate competition' },
        { county: 'Montgomery (Dayton)', notes: 'Deed-style, affordable inventory' },
        { county: 'Stark (Canton)', notes: 'Deed-style, requires 10% down day-of with 30 days to pay balance' },
      ],
      apnSearch: [
        { label: 'Ohio Dept of Taxation', url: 'https://tax.ohio.gov' },
        { label: 'Lucas County Auditor', url: 'https://www.co.lucas.oh.us/162/Auditor' },
      ],
      auctionLinks: [
        { label: 'Lucas County Sheriff Sales', url: 'https://www.co.lucas.oh.us' },
        { label: 'Franklin County Law Library - Foreclosure Guide', url: 'https://fclawlib.libguides.com/foreclosure/taxcertificates' },
      ],
      warnings: [
        'Confirm whether your target county runs deed sales or lien sales before researching further - the process is completely different',
        'The prior owner can still redeem right up until the court confirms the sale, so a "won" auction is not always final immediately',
        'Verify which liens, if any, survive the sheriff sale process in that specific county',
      ],
    },
  },
  {
    state: 'Ohio',
    type: 'Tax Lien',
    penalty: '12% (varies by county)',
    redemption: '1 year',
    schedule: 'Annual, often October-November',
    minReturn: '12%',
    notes: 'Major counties (Cuyahoga, Franklin) sell liens in bulk portfolios worth millions - not accessible to individual small investors.',
    rating: 'C',
    details: {
      overview: 'Around 34 of Ohio\'s 88 counties sell tax lien certificates, with major urban counties like Cuyahoga (Cleveland) and Franklin (Columbus) holding annual sales. The major caveat for individual investors: these counties increasingly sell certificates only in large bulk portfolios to institutional buyers, not as individual certificates to retail investors.',
      keyRules: [
        'Interest/penalty is typically around 12% annually, set by the county treasurer within state guidelines',
        'Redemption period is 1 year from the certificate sale',
        'If not redeemed within a year, the certificate holder may begin foreclosure proceedings',
        'Cuyahoga and Franklin Counties sell certificates in bulk - a single purchase can involve hundreds of properties worth $10-17 million',
        'Smaller, less populous Ohio counties are more likely to allow individual certificate purchases - verify before assuming',
      ],
      targetCounties: [
        { county: 'Cuyahoga (Cleveland)', notes: 'Bulk sales only - not realistic for individual investors without significant capital' },
        { county: 'Franklin (Columbus)', notes: 'Bulk sales only, similar scale to Cuyahoga' },
        { county: 'Hamilton (Cincinnati)', notes: 'Annual sales, typically October/November, verify bulk vs individual access' },
      ],
      apnSearch: [
        { label: 'Cuyahoga County Treasurer', url: 'https://cuyahogacounty.gov/treasury' },
        { label: 'Ohio Revised Code 5721.30-5721.43', url: 'https://codes.ohio.gov/ohio-revised-code/chapter-5721' },
      ],
      auctionLinks: [
        { label: 'Cuyahoga County Tax Lien Sales', url: 'https://cuyahogacounty.gov/treasury/delinquency/tax-lien-certificate-sales' },
      ],
      warnings: [
        'Cuyahoga and Franklin County lien sales are bulk, multi-million-dollar portfolio purchases - not realistic for most individual investors',
        'Before researching any Ohio county for lien investing, confirm whether it actually allows individual certificate purchases',
        'A tax lien purchaser does not get a guarantee that the property is free of other liens or title defects',
      ],
    },
  },
  {
    state: 'Missouri',
    type: 'Tax Deed',
    penalty: 'N/A - true deed (after 1-year redemption window)',
    redemption: '1 year (for first/second sale offerings)',
    schedule: 'Annual, held year-round across counties',
    minReturn: '20%',
    notes: 'County collector runs sales; properties typically get a 1-year redemption before deed issues. Third-offering sales can be redemption-free.',
    rating: 'B',
    details: {
      overview: 'Missouri uses a county collector-run tax deed process. Properties usually go through up to three annual sale offerings, with most carrying a 1-year redemption period for the first two offerings. By the third offering, a property may be sold with no redemption right at all.',
      keyRules: [
        'County collector or treasurer runs annual tax deed sales, with timing varying by county',
        'Most counties give a 1-year redemption period after the first or second sale offering',
        'A "third offering" sale (for properties unsold twice before) can be sold with no redemption period',
        'Premium bid method is standard - sold to the highest bidder above the minimum',
        'Always confirm which "offering" a property is on - it changes your redemption risk significantly',
      ],
      targetCounties: [
        { county: 'Jackson (Kansas City)', notes: 'Largest urban market on the Missouri side of KC' },
        { county: 'St. Louis County', notes: 'Large suburban market outside the city of St. Louis' },
        { county: 'Greene (Springfield)', notes: 'Southwest Missouri, less competition than KC/St. Louis' },
        { county: 'Boone (Columbia)', notes: 'College town market, steady rental demand' },
      ],
      apnSearch: [
        { label: 'Missouri Dept of Revenue - Property Tax', url: 'https://dor.mo.gov/taxation/property' },
        { label: 'Jackson County Collector', url: 'https://www.jacksongov.org' },
      ],
      auctionLinks: [
        { label: 'Tax Title Services Missouri Lists', url: 'https://www.taxtitleservices.com/nevada-tax-deed-sales-all-counties' },
        { label: 'St. Louis County Collector of Revenue', url: 'https://revenue.stlouisco.com' },
      ],
      warnings: [
        'Confirm which "offering" number a property is on before bidding - first/second offerings carry a 1-year redemption, third offerings may not',
        'A property redeemed during the 1-year window means you only get your money back plus interest, not the property',
        'Verify local county-specific rules, since Missouri tax sale procedures are administered at the county level and can vary',
      ],
    },
  },
  {
    state: 'Oklahoma',
    type: 'Tax Deed',
    penalty: 'N/A - true deed',
    redemption: 'Until the sale (none after)',
    schedule: 'Annual, June, county-run',
    minReturn: '20%',
    notes: 'County-run resale auctions held in June. Owner can redeem any time before the sale, but there is no redemption period after.',
    rating: 'B',
    details: {
      overview: 'Oklahoma is a tax deed state - the county sells the actual property rather than a lien against it. The county treasurer holds an annual "resale" auction, typically in June, for properties that have been delinquent long enough to qualify. Once sold, there is no post-sale redemption period.',
      keyRules: [
        'County treasurer runs the annual resale auction, typically held in June',
        'Properties become eligible after a multi-year delinquency period set by county/state procedure',
        'Owner can pay off back taxes and redeem any time before the auction, but not after',
        'Minimum bid generally covers back taxes, interest, penalties, and costs',
        'No tax lien certificates are sold in Oklahoma - this is deed-only',
      ],
      targetCounties: [
        { county: 'Oklahoma (OKC)', notes: 'Largest county, most inventory and competition' },
        { county: 'Tulsa', notes: 'Second largest market, moderate competition' },
        { county: 'Cleveland', notes: 'OKC suburbs, growing area' },
        { county: 'Comanche (Lawton)', notes: 'Smaller market, less competition' },
      ],
      apnSearch: [
        { label: 'Oklahoma County Assessor', url: 'https://www.oklahomacounty.org/365/Assessor' },
        { label: 'Oklahoma Tax Commission', url: 'https://oklahoma.gov/tax.html' },
      ],
      auctionLinks: [
        { label: 'Oklahoma County Treasurer Resale', url: 'https://www.oklahomacounty.org' },
        { label: 'Tulsa County Treasurer', url: 'https://www.tulsacounty.org' },
      ],
      warnings: [
        'No redemption after the sale - do your due diligence beforehand, not after winning',
        'Confirm the exact delinquency threshold and resale timing with the specific county, since administration varies',
        'Verify the property condition - county-held inventory can include unbuildable or landlocked lots',
      ],
    },
  },
  {
    state: 'Arkansas',
    type: 'Tax Deed',
    penalty: 'N/A - true deed',
    redemption: 'Until the sale (none after, with limited exceptions)',
    schedule: 'Year-round, county-run, oral auction',
    minReturn: '20%',
    notes: 'State Land Commissioner handles most sales after county process. Oral auction format, sales held throughout the year.',
    rating: 'B',
    details: {
      overview: 'Arkansas runs tax deed sales at the county level, with sales held throughout the year via oral public auction rather than on one fixed statewide date. Properties that remain unsold or are certified to the state may go through the Commissioner of State Lands office for resale.',
      keyRules: [
        'Tax deed sales are determined and run by individual counties, not on one statewide calendar',
        'Sales are conducted as oral, in-person public auctions',
        'The Arkansas Commissioner of State Lands office also conducts sales for certified/unsold parcels',
        'Owner can redeem by paying delinquent amounts before the sale; no standard redemption right after',
        'Minimum bid generally covers delinquent taxes, penalties, interest, and costs',
      ],
      targetCounties: [
        { county: 'Pulaski (Little Rock)', notes: 'Largest county, most inventory' },
        { county: 'Benton', notes: 'Northwest Arkansas, growing market (Bentonville/Rogers area)' },
        { county: 'Washington (Fayetteville)', notes: 'University town, steady rental demand' },
        { county: 'Sebastian (Fort Smith)', notes: 'Western Arkansas, lower competition' },
      ],
      apnSearch: [
        { label: 'Arkansas Commissioner of State Lands', url: 'https://www.cosl.org' },
        { label: 'Arkansas Assessment Coordination Division', url: 'https://www.dfa.arkansas.gov' },
      ],
      auctionLinks: [
        { label: 'Arkansas Commissioner of State Lands - Tax Sales', url: 'https://www.cosl.org/services/tax-land-sales' },
      ],
      warnings: [
        'Since sales happen throughout the year by county, track each county or the state land office directly rather than expecting one fixed date',
        'In-person, oral-auction format - confirm whether remote/online bidding is offered for your target county',
        'Research property access and condition carefully, as rural Arkansas parcels can be landlocked or have limited utility access',
      ],
    },
  },
  {
    state: 'Kansas',
    type: 'Tax Deed',
    penalty: 'N/A - true deed',
    redemption: 'Until the sale (none after)',
    schedule: 'Annual, county-run (varies by county)',
    minReturn: '20%',
    notes: 'No lien certificates sold. Properties not meeting minimum bid (taxes owed) are struck off to the county rather than generating surplus funds.',
    rating: 'C',
    details: {
      overview: 'Kansas is a tax deed state where county-run judicial tax sales auction off properties after a multi-year delinquency period. One notable feature: if no bidder meets the minimum bid (the taxes owed), the property is simply struck off to the county - there is no surplus-generating mechanism like in some other states.',
      keyRules: [
        'County-run judicial tax sale process, typically following a court order',
        'No tax lien certificates - Kansas is deed-only',
        'Properties not meeting the minimum bid are struck off to the county, not held for further bidding rounds with surplus',
        'Sale timing and procedure vary by county - no single fixed statewide date',
        'Owner can redeem by paying the full delinquent amount before the sale',
      ],
      targetCounties: [
        { county: 'Sedgwick (Wichita)', notes: 'Largest county, most inventory' },
        { county: 'Johnson', notes: 'Kansas City suburbs, higher quality inventory' },
        { county: 'Shawnee (Topeka)', notes: 'State capital area, moderate market' },
        { county: 'Douglas (Lawrence)', notes: 'University town, steady demand' },
      ],
      apnSearch: [
        { label: 'Kansas Dept of Revenue - Property Tax', url: 'https://www.ksrevenue.gov/proptax.html' },
        { label: 'Sedgwick County Treasurer', url: 'https://www.sedgwickcounty.org/treasurer' },
      ],
      auctionLinks: [
        { label: 'Johnson County Tax Sale Info', url: 'https://www.jocogov.org' },
      ],
      warnings: [
        'Rated C here mainly because procedures and inventory vary widely by county with limited statewide standardization - more research per-county is required than in many other states',
        'Confirm with the specific county whether a judicial confirmation step is required before you get clear title',
        'Rural Kansas parcels can be vacant agricultural land - confirm intended use before bidding',
      ],
    },
  },
  {
    state: 'Mississippi',
    type: 'Tax Lien',
    penalty: '1.5% per month (18% annualized)',
    redemption: '2 years',
    schedule: 'Annual - first Monday of April or last Monday of August',
    minReturn: '18%',
    notes: 'Bid up (premium bid) format - highest bidder wins but only earns interest on the face value, not the overbid. 2-year redemption.',
    rating: 'A',
    details: {
      overview: 'Mississippi is a tax lien state with a strong, well-documented process and a relatively high effective annual rate of 18%. Auctions use a premium bid format - the highest bidder wins, but any amount bid above the base tax debt earns no interest and is not returned if the lien is unredeemed.',
      keyRules: [
        'Premium bid format - highest bidder wins the lien',
        'Interest accrues at 1.5% per month (18% annualized) on the face value of the lien only, not on any overbid',
        'Redemption period is 2 years from the date of sale',
        'Auctions held the first Monday of April or the last Monday of August, depending on the county',
        'If no one bids, the lien is struck off to the state, which can later be purchased over-the-counter',
        'If unredeemed after 2 years, the Chancery Clerk issues a tax deed to the certificate holder - Mississippi does not run a separate, standalone deed auction',
      ],
      targetCounties: [
        { county: 'Hinds (Jackson)', notes: 'Largest county, most inventory' },
        { county: 'Harrison (Gulfport/Biloxi)', notes: 'Coastal market, tourism-driven economy' },
        { county: 'DeSoto', notes: 'Memphis suburbs, growing market' },
        { county: 'Rankin', notes: 'Jackson suburbs, higher quality inventory' },
      ],
      apnSearch: [
        { label: 'Mississippi Dept of Revenue - Property Tax', url: 'https://www.dor.ms.gov/property' },
        { label: 'Hinds County Chancery Clerk', url: 'https://www.hindscountyms.com' },
      ],
      auctionLinks: [
        { label: 'GovEase Mississippi Tax Sales', url: 'https://www.govease.com' },
      ],
      warnings: [
        'Do not bid up the premium expecting interest on it - only the base tax amount earns the 1.5% monthly rate',
        'Out of roughly 400 parcels sold at a typical county sale, historically fewer than 4% actually go unredeemed - plan around redemption as the likely outcome, not ownership',
        'Minors and persons of unsound mind get extended rights to redeem even after the standard 2-year window in some cases',
      ],
    },
  },
  {
    state: 'Alabama',
    type: 'Tax Lien',
    penalty: '12% per year',
    redemption: '3 years',
    schedule: 'Annual, typically first Monday in May',
    minReturn: '12%',
    notes: 'Tax lien certificates are sold (not deeds, as of 2018 law change). 3-year redemption at a flat 12% rate.',
    rating: 'B',
    details: {
      overview: 'Alabama sells tax lien certificates rather than the property itself. Owners have a 3-year window to redeem by paying back taxes, interest, fees, and penalties at a flat 12% annual rate. If unredeemed after 10 years, the certificate holder must have already initiated foreclosure or the lien expires and becomes void.',
      keyRules: [
        'Tax lien certificate sale - purchaser does not get any right, title, or interest in the property itself',
        'Redemption period is 3 years, with a flat 12% per year interest rate plus a $10 redemption fee',
        'Annual public auction typically held the first Monday in May',
        'Unsold liens can be purchased over-the-counter directly from the county at no more than 12% interest',
        'If the certificate holder does not begin a foreclosure action within 10 years, the lien expires and becomes void',
        'A successful foreclosure action results in a deed to the certificate holder - Alabama does not run a separate, standalone deed auction',
      ],
      targetCounties: [
        { county: 'Jefferson (Birmingham)', notes: 'Largest county, most inventory' },
        { county: 'Mobile', notes: 'Port city, coastal market' },
        { county: 'Tuscaloosa', notes: 'University town, steady demand' },
        { county: 'Madison (Huntsville)', notes: 'Tech-driven growth market' },
      ],
      apnSearch: [
        { label: 'Alabama Dept of Revenue - Property Tax', url: 'https://revenue.alabama.gov/property-tax' },
        { label: 'Jefferson County Revenue Commissioner', url: 'https://www.jccal.org' },
      ],
      auctionLinks: [
        { label: 'Mobile County Revenue Commission Tax Lien Sale', url: 'https://mobilecopropertytax.com/taxliensale' },
        { label: 'Tuscaloosa County Tax Lien Info', url: 'https://www.tuscco.com/tax-lien-sale-q-a' },
      ],
      warnings: [
        'A tax lien purchase gives you no right, title, or interest in the property - you are purchasing the debt only',
        'Foreclosure to actually obtain the property requires a separate court action, with a 10-year deadline to initiate it',
        'Verify whether the county you are targeting uses the auction format or only over-the-counter sales for remaining liens',
      ],
    },
  },
  {
    state: 'Colorado',
    type: 'Tax Lien',
    penalty: '9 points above federal discount rate (annual, varies)',
    redemption: '3 years',
    schedule: 'Annual, fall (Sept-Dec)',
    minReturn: '14% (2025 rate)',
    notes: 'Rate set each Sept 1st. After 2023 Tyler v. Hennepin ruling, county must auction the deed publicly rather than transfer it directly to the lienholder.',
    rating: 'B',
    details: {
      overview: 'Colorado is a tax lien state with a rate that changes annually - set each September 1st at 9 percentage points above the federal discount rate. The 2025 rate was 14%. A 2023 US Supreme Court ruling (Tyler v. Hennepin) changed Colorado procedure: counties must now sell the eventual treasurer\'s deed at public auction rather than automatically transferring it to the lien holder, to ensure property owners receive fair value.',
      keyRules: [
        'Interest rate is set annually each September 1st at 9 points above the federal discount rate',
        'Interest only accrues on the delinquent tax amount, not on any bid premium paid above it',
        'Redemption period is 3 years from the date of the lien sale',
        'After the 2023 Tyler v. Hennepin ruling, the county must hold a public auction for the treasurer\'s deed rather than transfer it directly - you are not guaranteed the property even after the redemption period expires',
        'Annual sales typically run September through December across the state',
      ],
      targetCounties: [
        { county: 'Denver', notes: 'Largest urban market, high competition' },
        { county: 'Jefferson', notes: 'Denver suburbs, online bidding available' },
        { county: 'Larimer (Fort Collins)', notes: 'Northern Colorado, steady demand' },
        { county: 'El Paso (Colorado Springs)', notes: 'Second largest city, less competition than Denver metro' },
      ],
      apnSearch: [
        { label: 'Colorado Dept of Local Affairs - Property Tax', url: 'https://cdola.colorado.gov' },
        { label: 'Jefferson County Treasurer', url: 'https://www.jeffco.us' },
      ],
      auctionLinks: [
        { label: 'Larimer County Tax Lien Sale', url: 'https://www.larimer.gov/treasurer' },
        { label: 'Colorado County Treasurers Association', url: 'https://www.cctpta.org' },
      ],
      warnings: [
        'Bidding large premiums above face value erodes your effective return since interest accrues only on the base tax amount',
        'Since 2023, winning a lien and waiting out the redemption period no longer guarantees you the property - you must also win the subsequent public deed auction',
        'Budget roughly $1,000 and 4-8 months for the treasurer\'s deed application process if a lien goes unredeemed',
      ],
    },
  },
  {
    state: 'New Mexico',
    type: 'Tax Deed',
    penalty: 'N/A - true deed',
    redemption: '2 years (to challenge sale validity, not a standard buy-back)',
    schedule: 'Annual, by county, often in winter/early spring',
    minReturn: '20%',
    notes: 'State (not county) runs the auction after ~3-4 years delinquency. Buyer gets the deed directly; no standard post-sale redemption.',
    rating: 'B',
    details: {
      overview: 'New Mexico is unusual in that the state Taxation and Revenue Department - not the county - runs the actual tax sale auction once county collection efforts fail and the property has been delinquent for roughly three to four years. The winning bidder gets a tax deed directly, and the prior owner does not get a standard redemption right, though they have 2 years to challenge the sale\'s validity in court.',
      keyRules: [
        'The state agency (not the county) conducts the public auction after county collection efforts are exhausted',
        'Properties become eligible after approximately 3-4 years of delinquency, depending on the source',
        'Winning bidder receives a tax deed directly at the auction - no certificate or waiting period',
        'Former owner has 2 years after the sale to challenge it in court if the process was not properly followed - this is not a standard redemption right',
        'Sale may also be subject to a 120-day federal IRS redemption period if applicable',
      ],
      targetCounties: [
        { county: 'Bernalillo (Albuquerque)', notes: 'Largest county, most inventory' },
        { county: 'Santa Fe', notes: 'Smaller inventory, higher property values' },
        { county: 'Dona Ana (Las Cruces)', notes: 'Southern NM, border region market' },
        { county: 'Sandoval', notes: 'Albuquerque suburbs, growing market' },
      ],
      apnSearch: [
        { label: 'NM Taxation and Revenue Dept - Property Tax', url: 'https://www.tax.newmexico.gov/businesses/property-tax-overview' },
        { label: 'Bernalillo County Assessor', url: 'https://www.bernco.gov/assessor' },
      ],
      auctionLinks: [
        { label: 'NM Delinquent Property Tax Auctions', url: 'https://www.tax.newmexico.gov/businesses/property-tax-overview/delinquent-property-tax-auctions' },
      ],
      warnings: [
        'The state does not warrant title to property sold at auction - only the tax lien is extinguished, other liens may survive',
        'The buyer is responsible for inspecting the property and reviewing the chain of title before bidding - the state does not do this for you',
        'The 2-year post-sale challenge window is about procedural validity, not a standard owner buy-back right - do not confuse the two',
      ],
    },
  },
  {
    state: 'Louisiana',
    type: 'Tax Lien',
    penalty: '0.7-1% per month + 5% flat penalty',
    redemption: '3 years (18 months if blighted/abandoned)',
    schedule: 'Annual, by parish, oral public auction',
    minReturn: '8.4-12% annualized',
    notes: 'Major 2026 law change (Act 557): switched from ownership bid-down to true interest-rate lien auctions. Bid down from 1%/month to a 0.7%/month floor.',
    rating: 'B',
    details: {
      overview: 'Louisiana significantly overhauled its tax sale system starting in 2026 under Act 557. The state moved from an older "ownership percentage bid-down" format to a true tax lien certificate auction, where investors bid down the monthly interest rate rather than ownership share. This is a recent change, so always confirm current-year rules with the parish tax collector.',
      keyRules: [
        'As of 2026, bidding starts at 1% per month (12% annualized) and can be bid down to a floor of 0.7% per month (8.4% annualized) - lowest rate wins',
        'A flat 5% penalty is added on top of the monthly interest, paid to the investor at redemption',
        'Standard redemption period is 3 years from when the tax lien is recorded, shortened to 18 months for blighted or abandoned property',
        'If unredeemed after the 3-year window (or 18 months for blighted property), the certificate holder can initiate judicial foreclosure',
        'Parishes (Louisiana\'s county equivalent) run the sales as oral public auctions',
        'A successful judicial foreclosure results in a deed (sheriff\'s sale) to satisfy the lien - Louisiana does not run a separate, standalone deed auction',
      ],
      targetCounties: [
        { county: 'Orleans Parish (New Orleans)', notes: 'Tax bills due Jan 31 here vs Dec 31 elsewhere - unique deadline, high activity' },
        { county: 'East Baton Rouge Parish', notes: 'State capital area, high activity market' },
        { county: 'Jefferson Parish', notes: 'New Orleans suburbs, large population' },
        { county: 'Caddo Parish (Shreveport)', notes: 'Northern Louisiana, less competition' },
      ],
      apnSearch: [
        { label: 'Louisiana Dept of Revenue', url: 'https://revenue.louisiana.gov' },
        { label: 'Orleans Parish Assessor', url: 'https://nolaassessor.com' },
      ],
      auctionLinks: [
        { label: 'Louisiana Law Help - Tax Sales', url: 'https://louisianalawhelp.org/resource/tax-sales' },
      ],
      warnings: [
        'This system changed significantly in 2026 - older guides describing the "ownership bid-down" format are now outdated, confirm current rules with the parish',
        '"Adjudicated" properties (no bidders at auction) follow a separate process under the same 2026 law update - research this separately if targeting unsold inventory',
        'Always verify which redemption period applies (3 years vs 18 months) since blighted/abandoned status changes your timeline significantly',
      ],
    },
  },
  {
    state: 'Idaho',
    type: 'Tax Deed',
    penalty: 'N/A - true deed',
    redemption: '14 months after deed issues (then none, except for irregularity claims)',
    schedule: 'Annual, county-run (often May hearing, summer auction)',
    minReturn: '20%',
    notes: 'No lien certificates sold. County takes title after 3 years delinquent, auctions within 14 months of the deed issuing.',
    rating: 'B',
    details: {
      overview: 'Idaho does not sell tax lien certificates at all - it is a pure tax deed state. After 3 years of delinquency, the county takes title to the property via tax deed, then must hold a public auction within 14 months of that deed issuing. The original owner can redeem up until the auction (or until the county contracts to sell), but not after.',
      keyRules: [
        'No lien certificates - Idaho is deed-only',
        'County takes title after 3 years of tax delinquency',
        'Public auction must be held no later than 14 months after the tax deed issues to the county',
        'Owner can redeem any time before the sale (or before a sale contract is signed) by paying delinquency, interest, and costs',
        'After the sale, there is a separate 1-year window where the sale can be challenged only if the tax deed process was procedurally irregular',
      ],
      targetCounties: [
        { county: 'Ada (Boise)', notes: 'Largest, most active county in the state' },
        { county: 'Canyon (Nampa/Caldwell)', notes: 'Boise metro area, growing fast' },
        { county: 'Kootenai (Coeur d\'Alene)', notes: 'Northern Idaho, strong recreational market' },
        { county: 'Bonner', notes: 'Smaller, scenic market, auction usually held in July' },
      ],
      apnSearch: [
        { label: 'Ada County Assessor', url: 'https://adacounty.id.gov/assessor' },
        { label: 'Idaho State Tax Commission', url: 'https://tax.idaho.gov' },
      ],
      auctionLinks: [
        { label: 'Bonner County Tax Deed Auction', url: 'https://www.bonnercountyid.gov/departments/Treasurer/FAQTaxDeeding' },
        { label: 'Shoshone County Tax Deed Auction', url: 'https://shoshonecounty.id.gov/tax-deed-auction' },
      ],
      warnings: [
        'Title insurance is typically unavailable for at least 3 years after a tax deed purchase - plan to hold rather than flip immediately',
        'You or a representative must be present in person to bid in most counties',
        'County receives a Quit Claim Deed style of title (not warranty) - a Quiet Title action may be needed before resale with financing',
      ],
    },
  },
  {
    state: 'Oregon',
    type: 'Tax Deed',
    penalty: 'N/A - true deed',
    redemption: '2 years (after judgment; total ~5 years from delinquency to county ownership)',
    schedule: 'County auctions held after county takes title (varies)',
    minReturn: '20%',
    notes: 'Unusually long timeline: 3 years to foreclosure eligibility, then a 2-year redemption period before the county even owns the property to auction.',
    rating: 'C',
    details: {
      overview: 'Oregon has one of the longest total timelines of any state covered here. Properties become eligible for foreclosure 3 years after taxes become delinquent, and once a court judgment is entered, a separate 2-year redemption period begins. Only after both periods pass does the county take full title and become free to auction the property - meaning roughly 5 years pass from the first missed payment to an investable county-owned auction.',
      keyRules: [
        'Real property becomes subject to foreclosure 3 years after the relevant tax year becomes delinquent (delinquency falls on May 16th each year)',
        'Once a circuit court grants judgment, a 2-year redemption period begins for the owner or other interested parties',
        'To redeem, the owner must pay all taxes and interest for the judgment years, plus a 5% penalty, in one lump sum - partial payments are not accepted',
        'At the end of the 2-year redemption period, the tax collector deeds the property to the county, and only then can it be sold at public auction',
        'The former owner generally retains the right to possess the property during the redemption period, but committing waste can forfeit that right',
      ],
      targetCounties: [
        { county: 'Multnomah (Portland)', notes: 'Largest county, most inventory once it reaches county-owned auction stage' },
        { county: 'Washington (Hillsboro)', notes: 'Portland suburbs, tech-driven growth' },
        { county: 'Deschutes (Bend)', notes: 'Central Oregon, recreational/vacation market' },
        { county: 'Klamath', notes: 'Southern Oregon, lower competition' },
      ],
      apnSearch: [
        { label: 'Oregon Dept of Revenue - Property Tax', url: 'https://www.oregon.gov/dor' },
        { label: 'Multnomah County Assessment', url: 'https://www.multco.us/assessment-taxation' },
      ],
      auctionLinks: [
        { label: 'Deschutes County Foreclosure Info', url: 'https://www.deschutes.org/finance/page/property-tax-collection-foreclosure-information' },
        { label: 'Klamath County Foreclosure Proceedings', url: 'https://www.klamathcounty.org/269/Foreclosure-Proceedings' },
      ],
      warnings: [
        'Rated C mainly due to the unusually long ~5-year total timeline before a property is even available at county auction - this is a patience-intensive market, not a fast one',
        'Properties at the eventual county auction stage have already cleared a long process, so inventory at any given sale tends to be smaller than faster-moving deed states',
        'Confirm each county\'s own auction schedule and method individually, since timing varies significantly after the county takes title',
      ],
    },
  },
  {
    state: 'Montana',
    type: 'Tax Lien',
    penalty: '10% per year',
    redemption: '2 years (then 36-day notice period to foreclose)',
    schedule: 'Annual, July',
    minReturn: '10%',
    notes: 'Straightforward 10% fixed-rate lien state. Annual sale in July, 2-year redemption.',
    rating: 'B',
    details: {
      overview: 'Montana is a fixed-rate tax lien state - investors earn a flat 10% annual return rather than competitive bid-down rates. The process is straightforward: an annual county auction each July, followed by a 2-year redemption period for the property owner.',
      keyRules: [
        'Fixed 10% annual interest rate - not bid down, unlike many other lien states',
        'Annual tax lien auctions held in July',
        'Redemption period is 2 years from the date of the lien sale',
        'After the redemption period expires, the certificate holder must take specific legal steps (typically a 36-day notice period) before obtaining a tax deed - Montana does not run a separate, standalone deed auction',
        'County treasurer administers the sale and redemption process',
      ],
      targetCounties: [
        { county: 'Yellowstone (Billings)', notes: 'Largest county, most inventory' },
        { county: 'Missoula', notes: 'University town, steady demand' },
        { county: 'Gallatin (Bozeman)', notes: 'Fast-growing market, higher property values' },
        { county: 'Silver Bow (Butte)', notes: 'Smaller market, historic mining town' },
      ],
      apnSearch: [
        { label: 'Montana Dept of Revenue - Property', url: 'https://mtrevenue.gov/property' },
        { label: 'Yellowstone County Treasurer', url: 'https://www.yellowstonecountymt.gov' },
      ],
      auctionLinks: [
        { label: 'Montana Dept of Labor & Industry State Land Sales', url: 'https://dli.mt.gov' },
      ],
      warnings: [
        'A fixed 10% rate means there is no competitive bidding to track - the rate is the rate regardless of demand for a given parcel',
        'Confirm the specific notice and foreclosure procedure with each county, since obtaining the deed after redemption expires involves formal legal steps',
        'Rural Montana parcels can be large and remote - confirm access and usability before bidding',
      ],
    },
  },
  {
    state: 'Wyoming',
    type: 'Tax Lien',
    penalty: '15% per year (statutory)',
    redemption: '4 years',
    schedule: 'Annual, July-September (varies by county)',
    minReturn: '15%',
    notes: 'High fixed 15% rate but a long 4-year redemption period - one of the longest of any lien state.',
    rating: 'B',
    details: {
      overview: 'Wyoming offers one of the higher fixed interest rates among tax lien states at 15% annually, but pairs it with one of the longest redemption periods at 4 years. This makes it a patient-capital play - good for tying up money long-term at a solid rate rather than a fast-turnaround strategy.',
      keyRules: [
        'Statutory interest rate of 15% per year on the certificate',
        'Redemption period is 4 years from the date of sale - longer than most other lien states',
        'Annual sales held between July and September depending on the county',
        'County treasurer runs the sale and administers redemption',
        'After the 4-year period, the certificate holder can pursue a tax deed through the proper legal process - Wyoming does not run a separate, standalone deed auction',
      ],
      targetCounties: [
        { county: 'Laramie (Cheyenne)', notes: 'Largest county, state capital area' },
        { county: 'Natrona (Casper)', notes: 'Second largest market' },
        { county: 'Teton (Jackson)', notes: 'High property values, resort market' },
        { county: 'Platte', notes: 'Smaller, rural market' },
      ],
      apnSearch: [
        { label: 'Wyoming Dept of Revenue - Property Tax', url: 'https://revenue.wyo.gov' },
        { label: 'Teton County Treasurer', url: 'https://www.tetoncountywy.gov' },
      ],
      auctionLinks: [
        { label: 'Platte County Tax Lien Sale', url: 'https://www.plattecountywyoming.com' },
        { label: 'Teton County Delinquent Tax Sale Info', url: 'https://www.tetoncountywy.gov/439/Delinquent-Tax-Sale-Information-Provisio' },
      ],
      warnings: [
        'The 4-year redemption period is significantly longer than most states - this ties up capital for an extended period before any path to ownership opens',
        'Confirm exact sale timing with the specific county, since the July-September window varies',
        'Rural Wyoming parcels can be remote with limited utility access - confirm usability before bidding',
      ],
    },
  },
  {
    state: 'North Dakota',
    type: 'Tax Deed',
    penalty: 'N/A - true deed',
    redemption: 'Until October 1st (then none)',
    schedule: 'Annual, third Tuesday of November',
    minReturn: '20%',
    notes: 'No lien certificates sold since 1999. County takes title after 4+ years delinquent, auctions countywide on the same day each November.',
    rating: 'B',
    details: {
      overview: 'North Dakota does not allow private lien-holder sales at all - since a 1999 law change, the only path is for the county itself to take title after a multi-year delinquency, then sell the property at a standardized annual auction. Nearly every county holds its sale on the same day: the third Tuesday of November.',
      keyRules: [
        'No tax lien sales to private investors since 1999 - this is purely a county-deed system',
        'County auditor gives foreclosure notice by June 1st for any property 4+ years tax delinquent',
        'Owner can redeem any time between the foreclosure notice and October 1st by paying all delinquent amounts',
        'After October 1st, tax title transfers to the county, and the property is sold at the annual sale on the third Tuesday of November',
        'IRS retains the right to redeem from the purchaser for up to 120 days after the sale',
      ],
      targetCounties: [
        { county: 'Cass (Fargo)', notes: 'Largest county, most inventory' },
        { county: 'Burleigh (Bismarck)', notes: 'State capital area' },
        { county: 'Williams (Williston)', notes: 'Oil boom region, can have unique inventory' },
        { county: 'Grand Forks', notes: 'University town, steady demand' },
      ],
      apnSearch: [
        { label: 'North Dakota Association of Counties', url: 'https://www.ndaco.org' },
        { label: 'Cass County Auditor', url: 'https://www.casscountynd.gov' },
      ],
      auctionLinks: [
        { label: 'Williams County Tax Foreclosure Properties', url: 'https://www.williamsnd.com/tax-foreclosure-properties' },
        { label: 'McKenzie County Tax Foreclosure Properties', url: 'https://mckenziecountynd.gov/tax-foreclosure-properties' },
      ],
      warnings: [
        'Properties are sold "AS IS" with no warranties and no guarantee of marketable title - inspection is entirely the buyer\'s responsibility',
        'You or a representative typically must be present in person to bid and pay at the time of sale',
        'Since nearly all counties sell on the same November date, you cannot easily bid across multiple counties in the same year without coordinating carefully',
      ],
    },
  },
  {
    state: 'South Dakota',
    type: 'Tax Lien',
    penalty: '10% per year',
    redemption: '3-4 years (varies by location)',
    schedule: 'Annual, county-run (varies)',
    minReturn: '10%',
    notes: 'Important: counties have not been required to sell certificates since a 2006 law - many no longer do. Confirm with the specific county before assuming lien availability.',
    rating: 'C',
    details: {
      overview: 'South Dakota is technically a tax lien state, but a 2006 law change means counties are no longer required to sell tax certificates - county commissioners must specifically opt back in by resolution to continue offering them. As a result, availability varies significantly by county, and you should never assume a given county still runs lien sales without checking first.',
      keyRules: [
        'Bid-down interest rate format historically, starting from a 10% per year statutory rate',
        'Redemption period is 3 years for property outside towns, 4 years (or longer depending on location specifics) in some cases - verify with the specific county',
        'Since 2006, counties are NOT required to sell tax certificates unless the board of county commissioners adopts a resolution waiving that prohibition',
        'Where certificates are still sold, the winning bidder is the one bidding the lowest interest rate while covering the full delinquent amount',
        'After redemption expires, specific notice procedures must be followed before a tax deed issues - South Dakota does not run a separate, standalone deed auction',
      ],
      targetCounties: [
        { county: 'Minnehaha (Sioux Falls)', notes: 'Largest county - verify current certificate sale status directly' },
        { county: 'Pennington (Rapid City)', notes: 'Second largest market - verify current certificate sale status directly' },
        { county: 'Lincoln', notes: 'Sioux Falls suburbs, growing area' },
      ],
      apnSearch: [
        { label: 'South Dakota Dept of Revenue', url: 'https://dor.sd.gov' },
        { label: 'Pennington County Treasurer', url: 'https://www.pennco.org' },
      ],
      auctionLinks: [
        { label: 'SD Dept of Revenue - County Treasurers', url: 'https://dor.sd.gov/government/county-treasurers' },
      ],
      warnings: [
        'Rated C specifically because many counties may no longer sell certificates at all since the 2006 opt-out law - confirm directly with the county treasurer before planning around this state',
        'Do not rely on older national guides that list South Dakota as a standard lien state without checking current county-level practice',
        'Redemption timelines can vary by whether property is in-town or rural - confirm specifics for your target parcel',
      ],
    },
  },
  {
    state: 'Nebraska',
    type: 'Tax Lien',
    penalty: '14% per year',
    redemption: '3 years',
    schedule: 'Annual, March (all counties)',
    minReturn: '14%',
    notes: 'All counties hold sales in March on a consistent statewide schedule. Unsold certificates available over-the-counter year-round.',
    rating: 'A',
    details: {
      overview: 'Nebraska is a clean, well-documented tax lien state with a solid 14% rate and a statewide annual sale schedule every March, making it easier to plan around than states with county-by-county timing. Unsold certificates can also be purchased over-the-counter throughout the year.',
      keyRules: [
        'Interest rate is 14% per year on the certificate',
        'Redemption period is 3 years from the date of the lien sale',
        'All counties hold their annual tax lien certificate sales in March',
        'Bids can be submitted by mail in advance of the sale in many counties',
        'Unsold certificates can be purchased over-the-counter directly from the county throughout the rest of the year',
        'After the 3-year redemption period, the investor can apply for a tax deed within 9 months, or pursue judicial foreclosure instead - Nebraska does not run a separate, standalone deed auction',
      ],
      targetCounties: [
        { county: 'Douglas (Omaha)', notes: 'Largest county, most inventory' },
        { county: 'Lancaster (Lincoln)', notes: 'State capital, university town' },
        { county: 'Sarpy', notes: 'Omaha suburbs, growing market' },
        { county: 'Hall (Grand Island)', notes: 'Central Nebraska, less competition' },
      ],
      apnSearch: [
        { label: 'Nebraska Dept of Revenue - Property Assessment', url: 'https://revenue.nebraska.gov/PAD' },
        { label: 'Douglas County Treasurer', url: 'https://www.douglascountytreasurer.org' },
      ],
      auctionLinks: [
        { label: 'Nebraska County Treasurers Association', url: 'https://www.nactaonline.com' },
      ],
      warnings: [
        'The investor does not automatically get the property after 3 years - specific legal steps (deed application or foreclosure) are required',
        'A misstatement in the redemption notice can invalidate the tax deed entirely, so notice procedure must be followed precisely',
        'Confirm whether mail-in bidding is accepted in your target county if you cannot attend in person',
      ],
    },
  },
  {
    state: 'Wisconsin',
    type: 'Tax Deed',
    penalty: 'N/A - true deed',
    redemption: '2 years from tax certificate issuance (then county can foreclose)',
    schedule: 'Varies by county, year-round',
    minReturn: '20%',
    notes: 'No lien certificates sold to investors. County takes full title via "In Rem" foreclosure, then sells for fair market value. Net proceeds must be returned to former owner by law.',
    rating: 'B',
    details: {
      overview: 'Wisconsin counties take full ownership of delinquent property through an "In Rem" foreclosure process rather than selling tax liens to investors. After the county owns the property outright, it sells for fair market value. Wisconsin law specifically requires counties to return any net sale proceeds (after taxes and costs) to the former owner - a consumer protection feature not present in every state.',
      keyRules: [
        'No tax liens are sold to private investors in Wisconsin - county takes title directly',
        'County issues a tax certificate each September 1st for unpaid prior-year taxes',
        'If taxes remain unpaid 2 years after the certificate issues, the county can begin "In Rem" foreclosure under Wisconsin Statute 75.521',
        'Once foreclosure judgment is entered, full title transfers to the county with no further redemption',
        'By law (Wis. Stat. 75.36), the county must pay any net proceeds from a later sale back to the former owner after deducting taxes, interest, penalties, and sale costs',
      ],
      targetCounties: [
        { county: 'Milwaukee', notes: 'Largest county, most inventory' },
        { county: 'Dane (Madison)', notes: 'State capital, university town, strong market' },
        { county: 'Oconto', notes: 'Smaller rural county, uses standard In Rem process' },
        { county: 'Iowa County, WI', notes: 'Smaller rural market, less competition' },
      ],
      apnSearch: [
        { label: 'Wisconsin Dept of Revenue - Property', url: 'https://www.revenue.wi.gov/Pages/Form2/property-home.aspx' },
        { label: 'Dane County Treasurer', url: 'https://lwrd.countyofdane.com' },
      ],
      auctionLinks: [
        { label: 'Dunn County Tax-Foreclosed Property Sales FAQ', url: 'https://dunncountywi.gov' },
        { label: 'Oconto County In Rem Foreclosure Info', url: 'https://www.ocontocountywi.gov/416/In-Rem-Foreclosure-of-Tax-Liens' },
      ],
      warnings: [
        'Properties sold by Wisconsin counties typically convey via Quit Claim Deed and are sold "AS IS" - inspect carefully before bidding',
        'Since there is no lien-investing path in Wisconsin, this state suits buy-the-property strategies only, not passive interest income',
        'Sale administration and timing vary significantly by county - there is no single statewide auction date',
      ],
    },
  },
  {
    state: 'Iowa',
    type: 'Tax Lien',
    penalty: '2% per month (24% annualized)',
    redemption: '~1 year 9 months (then 90-day notice to expire)',
    schedule: 'Annual, June (statewide)',
    minReturn: '24%',
    notes: 'One of the highest fixed lien rates in the country at 24% annualized. All counties hold sales in June.',
    rating: 'A',
    details: {
      overview: 'Iowa offers one of the highest fixed-rate returns of any tax lien state in the country: 2% per month, which compounds to an effective 24% annualized rate. All counties hold their annual sales in June, making this an easier state to plan around than ones with scattered county-by-county timing.',
      keyRules: [
        'Fixed interest rate of 2% per month on redemption (24% annualized) - not bid down',
        'Annual tax sales held in June, consistently across counties',
        'Owner has approximately 1 year and 9 months before the certificate holder can begin the 90-day notice of expiration of right of redemption',
        'Once the 90-day notice period passes without redemption, the certificate holder can obtain a tax deed - Iowa does not run a separate, standalone deed auction',
        'Certificate holders can also pay subsequent years\' delinquent taxes and add them to their certificate at the same 2% monthly rate',
      ],
      targetCounties: [
        { county: 'Polk (Des Moines)', notes: 'Largest county, most inventory' },
        { county: 'Linn (Cedar Rapids)', notes: 'Second largest market' },
        { county: 'Johnson (Iowa City)', notes: 'University town, steady demand' },
        { county: 'Black Hawk (Waterloo)', notes: 'Moderate competition' },
      ],
      apnSearch: [
        { label: 'Iowa Treasurers Association', url: 'https://www.iowatreasurers.org' },
        { label: 'Polk County Treasurer', url: 'https://www.polkcountyiowa.gov/treasurer' },
      ],
      auctionLinks: [
        { label: 'Iowa Tax And Tags - Tax Sale Info', url: 'https://www.iowataxandtags.org/property-tax/tax-sale' },
      ],
      warnings: [
        'Failing to send the required 90-day notice of expiration within 3 years of the original sale results in cancellation of your certificate and total loss of your investment - this deadline is strict',
        'A bank or other recorded lienholder can step in and redeem on the owner\'s behalf, which is a risk to factor in versus actual ownership odds',
        'You must have a recorded or qualifying interest in the parcel to redeem - confirm your own paperwork is in order if buying subsequent-year tax positions',
      ],
    },
  },
  {
    state: 'Illinois',
    type: 'Tax Lien',
    penalty: '18% per 6 months (up to 36% annualized first year)',
    redemption: '1-2.5 years depending on property type',
    schedule: 'Annual, county-run, Oct-Feb (Cook Co. in fall)',
    minReturn: '18%',
    notes: 'High potential yield but heavily bid down in competitive counties like Cook. Penalty scales up the longer redemption takes.',
    rating: 'B',
    details: {
      overview: 'Illinois has one of the most attractive statutory rate structures of any lien state - bidding starts at 18% per six-month period (effectively up to 36% annualized in the first year) and is bid down competitively. In practice, large institutional buyers in Cook County and other major counties often push winning bids down to 0-4%, so smaller counties tend to offer better realistic returns.',
      keyRules: [
        'Bidding starts at a maximum 18% penalty per six-month period and is bid down - lowest bidder wins',
        'Redemption period is 2.5 years for residential properties with 6 or fewer units, but only 1 year for commercial, industrial, or larger residential properties',
        'The penalty rate compounds every six months the lien remains unredeemed, not annually',
        'Annual county sales generally run between October and February, with Cook County typically in the fall',
        'If unredeemed, the certificate holder can petition the circuit court for a tax deed after the redemption period expires - Illinois does not run a separate, standalone deed auction',
      ],
      targetCounties: [
        { county: 'Cook (Chicago)', notes: 'Enormous inventory (100,000+ certificates some years) but heavily bid down by institutional buyers to 0-4%' },
        { county: 'DuPage', notes: 'Chicago suburbs, electronic sale, moderate competition' },
        { county: 'Lake County, IL', notes: 'Chicago suburbs, electronic sale process documented in detail by the county' },
        { county: 'Sangamon (Springfield)', notes: 'State capital area, smaller county, rates often stay closer to the 12-18% range' },
      ],
      apnSearch: [
        { label: 'Illinois Dept of Revenue - Property Tax', url: 'https://tax.illinois.gov/localgovernments/property.html' },
        { label: 'Cook County Treasurer', url: 'https://www.cookcountytreasurer.com' },
      ],
      auctionLinks: [
        { label: 'DuPage County Tax Redemption Process', url: 'https://www.dupagecounty.gov' },
        { label: 'Lake County, IL Tax Redemption Process', url: 'https://www.lakecountyil.gov/292/Tax-Redemption-Process' },
      ],
      warnings: [
        'In Cook County and other large counties, competitive institutional bidding routinely drives winning rates down to 0-4%, far below the statutory maximum - smaller counties are often more realistic for individual investors',
        'You must pay any subsequent years\' delinquent taxes to protect your position, and there are specific timing windows (about 4.5 months for the initial notice) you must follow exactly',
        'Confirm the property type carefully, since the redemption period differs significantly between small residential (2.5 years) and commercial/larger residential (1 year)',
      ],
    },
  },
  {
    state: 'Minnesota',
    type: 'Tax Deed',
    penalty: 'N/A - true deed (forfeiture to the state)',
    redemption: 'Until forfeiture (varies by classification)',
    schedule: 'Periodic, county-run, two-stage sale',
    minReturn: '20%',
    notes: 'No lien certificates. Property forfeits to the State of Minnesota after 3 years, then county runs a two-stage public auction within 6 months.',
    rating: 'B',
    details: {
      overview: 'Minnesota does not sell tax liens to investors. After about 3 years of delinquency, the property forfeits directly to the State of Minnesota, and the county then manages a structured two-stage sale process: first at estimated market value, then - if unsold after about 30 days - at a reduced minimum bid covering just the delinquent taxes, assessments, and costs.',
      keyRules: [
        'No tax lien certificates sold - this is a pure forfeiture-to-deed system',
        'Property forfeits to the State of Minnesota after approximately 3 years of delinquency',
        'Initial public auction held within about 6 months of forfeiture, starting at estimated market value',
        'If unsold after roughly 30 days, the minimum bid drops to just the delinquent taxes, assessments, penalties, interest, and costs',
        'Owner-occupants of small residential properties (4 units or fewer) get first opportunity to buy at the initial sale if they intend to occupy the property',
      ],
      targetCounties: [
        { county: 'Hennepin (Minneapolis)', notes: 'Largest county, structured two-sale process well documented online' },
        { county: 'Ramsey (St. Paul)', notes: 'Twin Cities market, similar process to Hennepin' },
        { county: 'St. Louis (Duluth)', notes: 'Northern Minnesota, more rural/land-heavy inventory' },
        { county: 'Otter Tail', notes: 'Rural lakes region, periodic sales, often land parcels' },
      ],
      apnSearch: [
        { label: 'Minnesota Dept of Revenue - Property Tax', url: 'https://www.revenue.state.mn.us/property-tax' },
        { label: 'Hennepin County Tax-Forfeited Land', url: 'https://www.hennepincounty.gov/services/property/tax-forfeited-land' },
      ],
      auctionLinks: [
        { label: 'Otter Tail County Tax-Forfeited Lands', url: 'https://ottertailcounty.gov/property-home/property-sales/tax-forfeited-lands' },
        { label: 'Washington County, MN Tax Forfeited Land', url: 'https://www.washingtoncountymn.gov/1140/Tax-Forfeited-Land' },
      ],
      warnings: [
        'Surplus proceeds above the delinquency amount must legally be returned to the former owner if they file a claim - factor this into your bidding strategy versus expecting to keep all upside',
        'Owner-occupants of small residential properties get priority at the first sale stage - confirm whether this applies before assuming you can win a given parcel',
        'There are no online land sale auctions in many counties - confirm format (in-person vs online) before planning to bid remotely',
      ],
    },
  },
  {
    state: 'Kentucky',
    type: 'Tax Lien',
    penalty: '12% per year',
    redemption: '1 year minimum (up to 11 years to foreclose)',
    schedule: 'Annual, county clerk, ~90-135 days after filing',
    minReturn: '12%',
    notes: 'Premium bid (highest bidder wins) on a fixed 12% rate. Sold as "certificates of delinquency" by the county clerk.',
    rating: 'A',
    details: {
      overview: 'Kentucky sells tax liens through county clerks as "certificates of delinquency," carrying a clean fixed 12% annual interest rate. Unlike many lien states, Kentucky uses a premium bid format where investors compete by paying more upfront for the certificate, rather than bidding the interest rate down.',
      keyRules: [
        'Certificates of delinquency bear simple interest at a fixed 12% per year (KRS 134.125)',
        'Premium bid method - county sets the starting bid (back taxes, penalties, interest, costs), and the highest bidder wins',
        'Sale is conducted by the county clerk, scheduled 90-135 days after unpaid tax claims are filed with the clerk',
        'Purchaser cannot file a lawsuit to collect until 1 year after the taxes became delinquent has passed',
        'No collection lawsuit can be filed after 11 years from the date taxes became delinquent',
        'A successful collection lawsuit can result in a deed to satisfy the debt - Kentucky does not run a separate, standalone deed auction',
      ],
      targetCounties: [
        { county: 'Jefferson (Louisville)', notes: 'Largest county, most certificates available' },
        { county: 'Fayette (Lexington)', notes: 'Second largest market' },
        { county: 'Kenton', notes: 'Cincinnati/Northern Kentucky metro area' },
        { county: 'Warren (Bowling Green)', notes: 'Growing market, moderate competition' },
      ],
      apnSearch: [
        { label: 'Kentucky Dept of Revenue - Property Tax', url: 'https://revenue.ky.gov/Property' },
        { label: 'Jefferson County Clerk', url: 'https://jeffersoncountyclerk.org' },
      ],
      auctionLinks: [
        { label: 'KY Dept of Revenue - Third Party Purchasers Manual', url: 'https://revenue.ky.gov/Documents/PotentialThirdPartyPurchasersManual.pdf' },
        { label: 'Kenton County Clerk Delinquent Tax', url: 'https://kentoncountykyclerk.com/delinquent-property-tax' },
      ],
      warnings: [
        'If you plan to purchase more than 3 certificates in a county or more than 5 statewide, or invest over $10,000, you must register as a Third Party Purchaser with the Department of Revenue first',
        'Certificates flagged for bankruptcy, pending litigation, or active payment plans are excluded from sale - verify a certificate is actually available before planning around it',
        'Large counties with 7,500+ certificates sell them in capped lots (no more than 50 for the first 4 rounds) - high-volume buying has structural limits even for big investors',
      ],
    },
  },
  {
    state: 'West Virginia',
    type: 'Tax Lien',
    penalty: '12% per year',
    redemption: '17 months (shortened from longer periods by recent law)',
    schedule: 'Annual, county sheriff/clerk-run',
    minReturn: '12%',
    notes: 'Competitive bidding, 12% rate. Redemption period was recently shortened to 17 months by state law change.',
    rating: 'B',
    details: {
      overview: 'West Virginia sells tax lien certificates with a 12% interest rate through competitive bidding. A relatively recent state law change shortened the redemption period to 17 months, faster than many other lien states, which can mean quicker turnaround for investors either toward redemption income or eventual deed.',
      keyRules: [
        'Interest rate is 12% per year on the certificate',
        'Redemption period is 17 months, shortened from longer periods under a recent legislative change',
        'Competitive bidding determines who wins the certificate at the annual sale',
        'County sheriff and county clerk\'s offices are both involved in the sale and subsequent deed process',
        'After the redemption period, the certificate holder must follow specific notice procedures before obtaining a deed - West Virginia does not run a separate, standalone deed auction',
      ],
      targetCounties: [
        { county: 'Kanawha (Charleston)', notes: 'Largest county, state capital, most activity for surplus fund claims historically' },
        { county: 'Berkeley (Martinsburg)', notes: 'Eastern panhandle, DC-commuter growth market' },
        { county: 'Monongalia (Morgantown)', notes: 'University town, steady demand' },
        { county: 'Cabell (Huntington)', notes: 'Western WV market' },
      ],
      apnSearch: [
        { label: 'West Virginia State Tax Dept - Property', url: 'https://tax.wv.gov' },
        { label: 'WV Property Search (mapWV)', url: 'https://mapwv.gov/parcel' },
      ],
      auctionLinks: [
        { label: 'WV Parcel Mapping - Delinquent Properties FAQ', url: 'https://mapwv.gov/parcel/docs/FAQ_Delinquent_Properties.pdf' },
      ],
      warnings: [
        'Confirm the current redemption period with the specific county, since this changed relatively recently and older sources may cite outdated timelines',
        'Surplus fund claims in West Virginia are filed with the state auditor rather than the county - this is a different process than most states covered here',
        'There is an unusual 18-month deadline tied to surplus claims - verify current procedure rather than assuming standard timelines apply',
      ],
    },
  },
  {
    state: 'Virginia',
    type: 'Tax Deed',
    penalty: 'N/A - true deed (judicial sale)',
    redemption: 'Until court confirmation of sale',
    schedule: 'Rolling - by locality, via Circuit Court',
    minReturn: '20%',
    notes: 'Judicial process only - localities cannot sell tax deeds outside Circuit Court foreclosure auctions. Takes 6 months to over a year per case.',
    rating: 'C',
    details: {
      overview: 'Virginia localities are only authorized to sell tax-delinquent property through court-supervised foreclosure auctions run by the locality\'s Circuit Court. This is a slower, judicial process - typically six months to over a year per property - rather than a standardized annual county sale.',
      keyRules: [
        'Only judicial sale is permitted - no administrative tax deed or lien certificate process exists in Virginia',
        'Process begins with notice to the owner and newspaper publication listing the property as foreclosure-eligible',
        'The Circuit Court must confirm the highest bid before the sale is final',
        'Typical payment terms: 10% in cash or certified funds on auction day, balance due in cash or certified funds within 30 days of court confirmation',
        'Property is conveyed via a Commissioner\'s Deed once the court confirms the sale',
      ],
      targetCounties: [
        { county: 'Fairfax', notes: 'Largest county by population, high property values, significant surplus fund activity' },
        { county: 'Virginia Beach', notes: 'Independent city, coastal market' },
        { county: 'Richmond (city)', notes: 'State capital, urban market' },
        { county: 'Prince William', notes: 'DC suburbs, growing market' },
      ],
      apnSearch: [
        { label: 'Virginia Dept of Taxation - Real Estate', url: 'https://www.tax.virginia.gov' },
        { label: 'Fairfax County Tax Administration', url: 'https://www.fairfaxcounty.gov/taxes' },
      ],
      auctionLinks: [
        { label: 'Prince William County Foreclosure Auction List', url: 'https://www.pwcva.gov/department/office-county-attorney/foreclosure-auction-list' },
      ],
      warnings: [
        'Rated C mainly because the judicial-only process makes timing unpredictable and slow - this is not a fast-turnaround state',
        'Each locality\'s Circuit Court Clerk may not maintain a centralized list of foreclosure properties - you often need to contact the County Attorney\'s office directly instead',
        'Confirm whether court confirmation is pending before assuming a winning bid is final, since the court retains final say',
      ],
    },
  },
  {
    state: 'Delaware',
    type: 'Tax Deed',
    penalty: 'N/A - true deed (sheriff sale via "monition" method)',
    redemption: 'Until sale (process varies by county)',
    schedule: 'Court-driven, county-run',
    minReturn: '20%',
    notes: 'Court-ordered sheriff sale process ("monition" method). Only 3 counties in the state, each running its own process.',
    rating: 'C',
    details: {
      overview: 'Delaware is a tax deed state where the taxing authority petitions the court for a judgment, and the court then directs the county sheriff to sell the property at public auction - a process Delaware law calls the "monition" method. With only 3 counties statewide, this is a small, niche market compared to most other states covered here.',
      keyRules: [
        'Taxing authority files a court petition; the court issues a writ directing the sheriff to conduct the public sale',
        'Sale goes to the highest bidder at the sheriff-run public auction',
        'After the sale, the Department of Finance or chief county financial officer must generally approve the final bid before it is final',
        'Delaware has only 3 counties total (New Castle, Kent, Sussex), so overall inventory is naturally smaller than larger states',
        'An alternative tax sale procedure also exists under a separate part of the Delaware Code for some collection scenarios',
      ],
      targetCounties: [
        { county: 'New Castle', notes: 'Largest and most populous county, includes Wilmington' },
        { county: 'Kent (Dover)', notes: 'State capital area, mid-size market' },
        { county: 'Sussex', notes: 'Southern Delaware, beach/coastal market' },
      ],
      apnSearch: [
        { label: 'Delaware Division of Revenue', url: 'https://revenue.delaware.gov' },
        { label: 'New Castle County Tax Records', url: 'https://www.nccde.org' },
      ],
      auctionLinks: [
        { label: 'Delaware Courts - Sheriff Sale Info', url: 'https://courts.delaware.gov' },
      ],
      warnings: [
        'Rated C mainly due to limited statewide inventory (only 3 counties) and the court-driven process making timing less predictable than administrative tax sale states',
        'Final bid approval by the Department of Finance or county financial officer is a real check, not a formality - do not assume the highest bid automatically wins',
        'Confirm with each specific county whether the standard monition method or the alternative tax sale procedure applies to your target property',
      ],
    },
  },
  {
    state: 'Maine',
    type: 'Tax Deed',
    penalty: 'N/A - true deed (municipal-run, not county)',
    redemption: '18 months from lien filing',
    schedule: 'Municipal-run, varies by town',
    minReturn: '20%',
    notes: 'Run by individual towns/municipalities, not counties. Strong owner-protection rules: surplus sale proceeds must go back to the former owner.',
    rating: 'C',
    details: {
      overview: 'Maine is unusual in that tax sales are administered by individual towns and municipalities rather than counties, since Maine has a strong tradition of local town governance. The state also has notably strong protections for former owners: any sale proceeds beyond what is owed in taxes, fees, and costs must legally be returned to them.',
      keyRules: [
        'Tax sales and foreclosures are run at the municipal (town) level, not the county level',
        'Standard redemption period is 18 months from the date the tax lien is filed',
        'If the property doesn\'t redeem, the municipal officer must list the property for sale with a licensed Maine real estate broker at the highest reasonable price',
        'After the sale, the municipality must pay the former owner any proceeds in excess of taxes owed, accrued costs, advertising, recording, and broker fees',
        'This sale-with-broker approach is different from most other states\' public auction model',
      ],
      targetCounties: [
        { county: 'Cumberland (Portland)', notes: 'Largest population center, most active town-level sales' },
        { county: 'York', notes: 'Southern Maine, coastal market' },
        { county: 'Penobscot (Bangor)', notes: 'Central/northern Maine market' },
        { county: 'Kennebec (Augusta)', notes: 'State capital area' },
      ],
      apnSearch: [
        { label: 'Maine Revenue Services - Property Tax', url: 'https://www.maine.gov/revenue/taxes/property-tax' },
        { label: 'Maine Municipal Association', url: 'https://www.memun.org' },
      ],
      auctionLinks: [
        { label: 'Maine Judicial Branch - Foreclosure Info', url: 'https://www.courts.maine.gov/help/foreclosure' },
      ],
      warnings: [
        'Rated C because this is a fragmented, town-by-town system (over 400 municipalities) rather than a standardized county process - expect heavy per-town research',
        'Since sales go through a licensed real estate broker rather than a public auction, the buying process looks more like a normal real estate purchase than a typical tax sale',
        'Confirm directly with each town clerk\'s office, since there is no single statewide auction calendar or portal',
      ],
    },
  },
  {
    state: 'District of Columbia',
    type: 'Tax Lien',
    penalty: '18% per year (1.5% per month)',
    redemption: '6 months minimum (lawsuit can be filed after)',
    schedule: 'Annual, July',
    minReturn: '18%',
    notes: 'Premium bid-up format. About 98% of liens redeem. Overbid surplus refunded to investor if redeemed - a feature not found in most states.',
    rating: 'A',
    details: {
      overview: 'Washington DC runs a single, well-documented annual tax lien sale every July. It uses a premium bid-up format with a strong 18% rate, and uniquely, if the property is redeemed, the District refunds the investor\'s overbid surplus - a feature that protects investor capital better than most other premium-bid states.',
      keyRules: [
        'Interest rate is 18% per year (1.5% per month) on the delinquent tax amount',
        'Premium bid-up format - starting bid is the amount owed, then bid up; highest bidder wins but earns no interest on the premium portion',
        'Properties with under $2,500 in delinquent taxes that are owner-occupied residential are exempt from sale',
        'Redemption period is 6 months minimum before a foreclosure lawsuit can be filed; an additional 6 months typically follows before judgment',
        'If the property redeems, the city refunds the overbid surplus to the winning bidder - this is unusual and investor-friendly compared to most states',
        'If unredeemed and the foreclosure lawsuit succeeds, the court directs the Mayor to issue a deed to the purchaser - DC does not run a separate, standalone deed auction',
      ],
      targetCounties: [
        { county: 'Washington, DC (single jurisdiction)', notes: 'No county subdivisions - the entire District is one tax sale market, with annual auction in July' },
      ],
      apnSearch: [
        { label: 'DC Office of Tax and Revenue (OTR)', url: 'https://otr.cfo.dc.gov' },
        { label: 'DC Real Property Tax Lien Sale Info', url: 'https://otr.cfo.dc.gov/page/real-property-tax-lien-sale-and-resources' },
      ],
      auctionLinks: [
        { label: 'DC OTR Real Property Tax Lien Sale', url: 'https://otr.cfo.dc.gov/page/real-property-tax-lien-sale-and-resources' },
      ],
      warnings: [
        'About 98% of liens successfully redeem, so plan around earning the 18% interest as your primary outcome, not acquiring the property',
        'Federal government liens can take priority and are relatively common in DC - factor this risk in versus other states',
        'You must wait 4-6 months before starting the notification process and meet strict 90-day pre-filing notice requirements, or you forfeit your right to foreclose',
      ],
    },
  },
  {
    state: 'New York',
    type: 'Tax Lien',
    penalty: 'Varies (NYC ~18%; upstate counties vary)',
    redemption: 'Varies significantly by locality',
    schedule: 'Varies - NYC annual; upstate counties vary widely',
    minReturn: 'Varies',
    notes: 'IMPORTANT: NYC sells liens only in bulk to authorized trusts, not to individual investors. Nassau and Suffolk Counties (Long Island) sell directly to individual investors instead.',
    rating: 'C',
    details: {
      overview: 'New York is genuinely fragmented and requires careful per-locality research - there is no single statewide process. New York City sells its tax liens exclusively to a single authorized buyer (NYCTL Trusts), not to individual investors, while Nassau and Suffolk Counties on Long Island sell directly to third-party investors who bid competitively. Many upstate counties instead run conventional tax deed foreclosure auctions rather than lien sales at all.',
      keyRules: [
        'New York City sells liens only to one authorized buyer at a time (a single trust) - individual investors cannot purchase NYC tax liens directly',
        'Nassau and Suffolk Counties sell tax liens directly to third-party investors through competitive bidding - this is the realistic entry point for individual investors in the NY metro area',
        'Many upstate counties (e.g., Jefferson County) instead foreclose directly and sell the property itself at auction, extinguishing pre-existing tax liens in the process',
        'NYC liens carry roughly 18% annual interest once sold, though individual investors cannot access this directly',
        'Redemption periods, interest rates, and sale formats vary significantly by which of these three paths applies to your target county',
      ],
      targetCounties: [
        { county: 'Nassau (Long Island)', notes: 'Sells liens directly to individual third-party investors - the most accessible NY lien market' },
        { county: 'Suffolk (Long Island)', notes: 'Same direct-to-investor model as Nassau' },
        { county: 'Jefferson (upstate)', notes: 'Conventional tax deed foreclosure auction model, not a lien sale' },
        { county: 'New York City (5 boroughs)', notes: 'NOT individually accessible - liens sold only in bulk to authorized trusts' },
      ],
      apnSearch: [
        { label: 'NY Office of Real Property Tax Services', url: 'https://www.tax.ny.gov/research/property' },
        { label: 'Nassau County Treasurer', url: 'https://www.nassaucountyny.gov/agencies/Treasurer' },
      ],
      auctionLinks: [
        { label: 'NYC Dept of Finance - Lien Sale Info', url: 'https://www.nyc.gov/site/finance/property/property-lien-sales.page' },
        { label: 'Jefferson County, NY Tax Foreclosure FAQ', url: 'https://www.jeffersoncountyny.gov/departments/CountyAttorney/frequently-asked-tax-foreclosure-and-auction-questions' },
      ],
      warnings: [
        'Do not assume you can buy NYC tax liens directly - the City explicitly does not sell to individuals or the general public, only to one authorized institutional buyer',
        'Rated C specifically because "New York" as a single state entry is misleading - Nassau/Suffolk, NYC, and upstate counties are functionally three different systems that must be researched separately',
        'A 2023 Supreme Court ruling (Tyler v. Hennepin) is actively reshaping how New York handles surplus proceeds from tax foreclosure sales - rules may continue to evolve',
      ],
    },
  },
  {
    state: 'New Jersey',
    type: 'Tax Lien',
    penalty: 'Up to 18% + 2-6% redemption penalty',
    redemption: '2 years (6 months if municipality holds the lien)',
    schedule: 'Annual, by municipality (566 separate sales statewide)',
    minReturn: '18%',
    notes: 'Extremely fragmented - 566 municipalities, each running its own sale, mostly in-person. After 0% bid, investors bid premiums instead, held by the municipality for 5 years.',
    rating: 'B',
    details: {
      overview: 'New Jersey taxes are administered at the municipal level, meaning there are 566 separate tax sales statewide rather than one per county. The bidding format is distinctive: investors first bid down the interest rate from 18%, and once it reaches 0%, they switch to bidding cash premiums instead, which are held by the municipality (not earning interest) for five years.',
      keyRules: [
        'Bidding starts at 18% interest and is bid down; once it hits 0%, bidders compete via cash premiums instead',
        'Premiums paid above the lien amount are held by the municipality for 5 years and are not returned with interest',
        'Redemption period is 2 years if a private investor holds the certificate, but only 6 months if the municipality itself is the lien holder',
        'On redemption, an additional 2%, 4%, or 6% penalty applies depending on the certificate amount (over and above the bid interest rate)',
        'Lien holder must record the Tax Sale Certificate with the county clerk within 90 days of the sale',
        'If unredeemed, the lien holder must file a foreclosure action in Superior Court to obtain title - New Jersey does not run a separate, standalone deed auction',
      ],
      targetCounties: [
        { county: 'Bergen', notes: 'Largest county by population, many municipalities, high property values' },
        { county: 'Essex (Newark)', notes: 'Urban core, dense municipal tax sale activity' },
        { county: 'Mercer (Trenton)', notes: 'State capital area' },
        { county: 'Ocean', notes: 'Shore communities, seasonal property mix' },
      ],
      apnSearch: [
        { label: 'NJ Division of Taxation - Property', url: 'https://www.nj.gov/treasury/taxation/lpt' },
        { label: 'NJ Division of Local Government Services', url: 'https://www.nj.gov/dca/divisions/dlgs' },
      ],
      auctionLinks: [
        { label: 'NJ Tax Sale Law Overview (DLGS)', url: 'https://www.nj.gov/dca/divisions/dlgs/programs/tax_collector_docs/elements_of_tax_sales_nj.pdf' },
      ],
      warnings: [
        'With 566 separate municipal sales, there is no single statewide calendar or portal - expect significant per-municipality research even within one county',
        'Most auctions are in-person, oral, "call out" format - only about 10-20 municipalities currently offer online bidding',
        'You acquire no rights to enter, inspect, or act as landlord on the property as a lien holder - this is purely a financial position until and unless foreclosure is completed',
      ],
    },
  },
  {
    state: 'Connecticut',
    type: 'Tax Deed',
    redeemableDeed: true,
    penalty: 'N/A - true deed (18% redemption rate on purchase price)',
    redemption: '6 months (10 months if federal lien involved)',
    schedule: 'Non-judicial, by municipality (100+ towns conduct sales)',
    minReturn: '20%',
    notes: 'Town-run non-judicial tax sales. Property passes free and clear if unredeemed after 6 months. Overbids returned to bidder if redeemed, or sent to court if not.',
    rating: 'B',
    details: {
      overview: 'Connecticut runs non-judicial tax sales at the municipal level - more than 100 towns conduct their own sales rather than going through a county or court process. If the owner doesn\'t redeem within 6 months, the property passes to the winning bidder free and clear of most prior liens, making this one of the faster deed-acquisition states covered here.',
      keyRules: [
        'More than 100 Connecticut municipalities conduct non-judicial tax sales directly',
        'Redemption period is 6 months from the date of the auction; extended to 10 months if a federal government lien exists on the property',
        'During redemption, the owner pays back taxes/charges plus 18% per annum interest on the purchase price the winning bidder paid',
        'If unredeemed, title passes to the purchaser free and clear of prior liens and mortgages, with limited exceptions (e.g., IRS liens)',
        'Any overbid above the minimum is returned to the bidder if the property is redeemed, or turned over to the Superior Court for distribution if the property is not redeemed',
      ],
      targetCounties: [
        { county: 'Fairfield', notes: 'High property values, Western CT/NYC commuter area' },
        { county: 'Hartford', notes: 'State capital region, larger municipal market' },
        { county: 'New Haven', notes: 'Mixed urban/suburban inventory' },
        { county: 'Middlesex', notes: 'Smaller, more rural municipalities' },
      ],
      apnSearch: [
        { label: 'Connecticut Office of Policy and Management', url: 'https://portal.ct.gov/opm' },
        { label: 'CT Tax Collectors Association', url: 'https://ct-tax.org' },
      ],
      auctionLinks: [
        { label: 'CT Tax Collectors - Collecting in CT Guide', url: 'https://ct-tax.org/collecting-in-ct' },
      ],
      warnings: [
        'Connecticut has no county government structure for this purpose - tax sales are run entirely at the town level, so research and track each town individually',
        'A summary procedure with different rules applies if a property\'s fair market value is under $100,000 and less than what is owed - confirm which process applies to your target property',
        'Federal tax liens extend the redemption period to 10 months instead of 6, and the federal government redeems at a lower rate - factor this into your timeline planning',
      ],
    },
  },
  {
    state: 'Massachusetts',
    type: 'Tax Lien',
    penalty: '16% per year',
    redemption: '6 months to 1 year (varies by municipality)',
    schedule: 'Varies - annual, quarterly, or as-needed by municipality',
    minReturn: '16%',
    notes: 'Decentralized municipal system. 16% rate with a relatively short redemption window makes this a faster-turnaround lien state.',
    rating: 'B',
    details: {
      overview: 'Massachusetts offers a strong 16% annual interest rate on tax liens with a comparatively short redemption period - typically 6 months to 1 year depending on the specific municipality. Like several other New England states, the process is decentralized: cities and towns set their own auction schedules rather than following one statewide calendar.',
      keyRules: [
        'Interest rate is 16% per annum on the tax lien',
        'Redemption period typically ranges from 6 months to 1 year, varying by municipality and the specifics of the lien',
        'Auction frequency varies significantly - some municipalities hold annual sales, others quarterly or only as-needed',
        'Investor activity statewide is generally described as moderate, meaning less institutional competition than states like Florida or Illinois',
        'This decentralized structure means proactive, municipality-by-municipality research is necessary rather than relying on one statewide source',
        'If unredeemed, the lien holder must petition Land Court to foreclose and obtain a deed - Massachusetts does not run a separate, standalone deed auction',
      ],
      targetCounties: [
        { county: 'Suffolk (Boston)', notes: 'Largest urban market, highest property values' },
        { county: 'Middlesex', notes: 'Largest county by population, Boston suburbs' },
        { county: 'Worcester', notes: 'Central Massachusetts, more affordable inventory' },
        { county: 'Hampden (Springfield)', notes: 'Western Massachusetts, less competition' },
      ],
      apnSearch: [
        { label: 'Massachusetts Dept of Revenue - Property Tax', url: 'https://www.mass.gov/topics/property-taxes' },
        { label: 'Massachusetts Municipal Association', url: 'https://www.mma.org' },
      ],
      auctionLinks: [
        { label: 'Massachusetts Tax Lien Investing Overview', url: 'https://lienscoutpro.com/states/massachusetts' },
      ],
      warnings: [
        'There is no single statewide auction calendar - confirm directly with each city/town treasurer or collector\'s office for actual sale dates',
        'The exact redemption period (6 months vs 1 year) depends on municipal specifics - do not assume one timeline applies statewide',
        'As with other municipal-run New England states, processes and forms can vary meaningfully between neighboring towns',
      ],
    },
  },
  {
    state: 'Vermont',
    type: 'Tax Deed',
    penalty: 'N/A - true deed (1% per month redemption interest)',
    redemption: '1 year',
    schedule: 'Varies by town (237 towns + 9 cities collect taxes)',
    minReturn: '12%',
    notes: 'Highly fragmented - 246 separate taxing towns/cities. Recent legislative reform added guardrails after a high-profile case of a farm sold far below value.',
    rating: 'C',
    details: {
      overview: 'Vermont tax sales are run by individual towns - there are 237 towns and 9 cities statewide that each collect their own property taxes and can run their own sale. A widely publicized 2024 case, where a couple\'s half-million-dollar farm was sold for just $24,000 over an $8,100 tax debt, prompted new state legislation adding guardrails to the process.',
      keyRules: [
        'Each of the 246 towns/cities statewide collects its own taxes and may run its own tax sale - no single county-level system exists',
        'Redemption period is 1 year from the date of sale',
        'During redemption, the owner pays the purchase price plus interest at 1% per month',
        'If unredeemed after 1 year, the winning bidder receives a tax collector\'s deed and becomes the new owner',
        'Recent (2024) legislative reform added new guardrails to the tax sale process following public scrutiny of cases where properties sold for a fraction of their value',
      ],
      targetCounties: [
        { county: 'Chittenden (Burlington)', notes: 'Largest population center, most active market' },
        { county: 'Washington (Montpelier)', notes: 'State capital area' },
        { county: 'Rutland', notes: 'Central Vermont, smaller market' },
        { county: 'Windsor', notes: 'Smaller towns, rural inventory' },
      ],
      apnSearch: [
        { label: 'Vermont Dept of Taxes - Property Tax', url: 'https://tax.vermont.gov/property' },
        { label: 'Vermont League of Cities and Towns', url: 'https://www.vlct.org' },
      ],
      auctionLinks: [
        { label: 'Vermont Public - Tax Sale Reporting', url: 'https://www.vermontpublic.org/local-news/2024-03-18/vermont-towns-use-tax-sales-to-collect-late-payments-but-critics-say-the-process-lacks-guardrails' },
      ],
      warnings: [
        'Rated C mainly due to the extreme town-by-town fragmentation (246 separate taxing entities) and a documented history of properties selling for far below market value relative to the tax debt owed',
        'Given the recent legislative changes, confirm you are working from the current post-reform rules rather than older guides',
        'A title examiner will need to verify notice was properly given and that the 1-year statute of limitations has passed before title is considered fully marketable',
      ],
    },
  },
  {
    state: 'New Hampshire',
    type: 'Tax Deed',
    penalty: 'N/A - true deed (municipality-held, not investor-purchased)',
    redemption: '3 years after municipality takes the deed',
    schedule: 'Municipality-run, as needed (not a standard public auction)',
    minReturn: '20%',
    notes: 'Unusual structure: municipality itself takes the tax deed after 2 years, then has a separate 3-year window to sell. No private lien-buying step exists.',
    rating: 'C',
    details: {
      overview: 'New Hampshire works differently from most deed states: rather than investors buying a lien or bidding at a tax sale, the municipality itself takes the tax deed directly after 2 years of unpaid taxes following the original lien execution. The municipality can then sell the property at its discretion within a following 3-year window, after sending proper notice to the former owner.',
      keyRules: [
        'No public lien-certificate sale to investors exists - the tax lien transfers from the original execution directly to the municipality',
        'If taxes remain unpaid for 2 years after that transfer, the tax collector must tender a deed of the property to the municipality (RSA 80:76)',
        'Former owner then has 3 years after the municipality takes the deed to redeem by paying past-due taxes and fees',
        'During this 3-year window, the municipality may sell the property, but must send a redemption notice to the former owner and any mortgage holder at least 90 days before the sale',
        'Following the 2020 Polonsky v. Town of Bedford ruling, municipalities may have a constitutional obligation to pay former owners any excess sale proceeds beyond what was owed',
      ],
      targetCounties: [
        { county: 'Hillsborough (Manchester)', notes: 'Largest county, most populous municipalities' },
        { county: 'Rockingham', notes: 'Seacoast region, higher property values' },
        { county: 'Merrimack (Concord)', notes: 'State capital area' },
        { county: 'Strafford', notes: 'Smaller market, university town influence (Durham)' },
      ],
      apnSearch: [
        { label: 'NH Dept of Revenue Administration - Property', url: 'https://www.revenue.nh.gov/municipal-property' },
        { label: 'NH Municipal Association', url: 'https://www.nhmunicipal.org' },
      ],
      auctionLinks: [
        { label: 'NH Municipal Association - Tax Deeded Property FAQ', url: 'https://www.nhmunicipal.org/town-city-article/tax-deeded-property-legal-q' },
      ],
      warnings: [
        'Rated C because there is no standardized public sale process - properties become available at each municipality\'s discretion within its 3-year window, not on a predictable calendar',
        'Since 2020, excess proceeds rules have shifted significantly due to court rulings - confirm current municipal practice rather than relying on the older statute alone',
        'Contact individual town/city offices directly, since there is no centralized statewide tax-deeded property listing',
      ],
    },
  },
  {
    state: 'Hawaii',
    type: 'Tax Deed',
    redeemableDeed: true,
    penalty: 'N/A - true deed (12% per year redemption interest)',
    redemption: '1 year (from sale or deed recording, whichever is later)',
    schedule: 'Annual, by county (only 4 counties statewide)',
    minReturn: '12%',
    notes: 'Redeemable tax deed state - clean and well-documented. Only 4 counties statewide. No absentee bidding allowed, but representatives can bid on your behalf.',
    rating: 'B',
    details: {
      overview: 'Hawaii is a redeemable tax deed state with a clean, well-documented process and only 4 counties statewide (Honolulu, Hawaii, Maui, Kauai), making it much simpler to research than most other states covered here. The purchaser gets a deed immediately after the sale, but the former owner has up to 1 year to redeem by paying the purchase price plus 12% annual interest.',
      keyRules: [
        'Purchaser receives a deed at the sale, subject to the former owner\'s 1-year right of redemption',
        'Redemption period is 1 year from the date of sale, or 1 year from the deed\'s recording date if the deed was not recorded within 60 days of the sale',
        'To redeem, the former owner pays the purchase price, the purchaser\'s costs and expenses, plus 12% annual interest',
        'Only 4 counties administer real property tax sales statewide, simplifying the research process significantly',
        'No absentee bidding is permitted, but you may send a representative to bid on your behalf',
      ],
      targetCounties: [
        { county: 'Honolulu (Oahu)', notes: 'By far the largest population and market, most inventory' },
        { county: 'Hawaii (Big Island)', notes: 'Second largest, more land-heavy inventory' },
        { county: 'Maui', notes: 'Resort/vacation market, higher property values' },
        { county: 'Kauai', notes: 'Smallest market, less competition' },
      ],
      apnSearch: [
        { label: 'Hawaii Bureau of Conveyances', url: 'https://dlnr.hawaii.gov/boc' },
        { label: 'Maui County Real Property Tax', url: 'https://www.mauicounty.gov' },
      ],
      auctionLinks: [
        { label: 'Maui County Tax Sale FAQ', url: 'https://www.mauicounty.gov/faq.aspx?TID=115' },
        { label: 'Honolulu Real Property Assessment', url: 'https://www.realpropertyhonolulu.com' },
      ],
      warnings: [
        'The county will not warrant title or represent you in any title disputes - properties are sold "as is" with no guarantee other liens are cleared',
        'It can take several months after the sale to receive the recorded deed, which affects when your redemption-period clock and resale options begin',
        'Confirm whether your target county allows a representative to bid on your behalf in advance, since in-person attendance (by you or your representative) is required',
      ],
    },
  },
  {
    state: 'Alaska',
    type: 'Tax Deed',
    penalty: 'N/A - true deed (no public auction at foreclosure stage itself)',
    redemption: 'Until the borough\'s later sale of the property',
    schedule: 'Borough-run, periodic (often every 1-2 years)',
    minReturn: '20%',
    notes: 'Unusual two-stage structure: foreclosure transfers property directly to the borough (no public sale at that point), then the borough later auctions its inventory periodically.',
    rating: 'C',
    details: {
      overview: 'Alaska works differently from most tax deed states. When a property is tax-foreclosed, there is no public sale at that stage - the property transfers directly to the borough or municipality. Only later, when the borough decides to sell off its accumulated inventory (often every 1-2 years), does a public auction happen. The former owner can typically repurchase their property at any point before that eventual sale.',
      keyRules: [
        'Tax foreclosure itself is not a public sale - the court enters judgment and the property transfers directly to the municipality/borough',
        'The former owner\'s right to redeem generally continues until the borough actually sells or contracts to sell the property',
        'Boroughs hold periodic sales of their accumulated tax-foreclosed inventory - Fairbanks North Star Borough, for example, holds sales at least once every two years',
        'Properties are typically sold "AS IS - WHERE IS" with no title warranty from the borough',
        'A buyer\'s premium (e.g., 5% in some boroughs) is commonly added to the winning bid for online auction services',
      ],
      targetCounties: [
        { county: 'Anchorage (Municipality)', notes: 'Largest market, uses auctions, negotiated sales, and sealed bids depending on the property' },
        { county: 'Fairbanks North Star Borough', notes: 'Second largest market, sales at least every 2 years' },
        { county: 'Matanuska-Susitna (Mat-Su)', notes: 'Growing area near Anchorage, online bidding via Public Surplus' },
        { county: 'Kenai Peninsula', notes: 'More rural inventory, recreational/remote land mix' },
      ],
      apnSearch: [
        { label: 'Municipality of Anchorage Real Estate Services', url: 'https://www.muni.org/Departments/hlb/pages/realestateservices.aspx' },
        { label: 'Fairbanks North Star Borough Tax Foreclosure', url: 'https://www.fnsb.gov/169/Tax-Foreclosure' },
      ],
      auctionLinks: [
        { label: 'Mat-Su Borough Tax Foreclosure Sales', url: 'https://matsugov.us' },
      ],
      warnings: [
        'Rated C because the redemption right effectively continues until the borough\'s eventual resale, which can be unpredictable in timing - this is not a fast or standardized process',
        'Many Alaska parcels, especially outside Anchorage/Fairbanks, can be remote with limited access - verify usability and access before bidding, not after',
        'The borough does not guarantee clear title - budget for a title company consultation if you want title insurance after winning a parcel',
      ],
    },
  },
  {
    state: 'Rhode Island',
    type: 'Tax Deed',
    redeemableDeed: true,
    penalty: 'N/A - true deed (hybrid; 10% + 1%/month redemption)',
    redemption: '1 year',
    schedule: 'Annual, by municipality (~39 cities/towns)',
    minReturn: '10%',
    notes: 'Hybrid tax deed - buyer gets a deed at auction subject to 1-year redemption. Unusual fractional-interest sale mechanic similar to Utah. Run by ~39 municipalities, not counties.',
    rating: 'B',
    details: {
      overview: 'Rhode Island runs a hybrid tax deed process at the municipal level - about 39 cities and towns hold their own annual tax sales rather than going through a county system. An unusual feature: at auction, only the smallest necessary undivided percentage of the property (minimum 1%) is sold to cover the debt, with the whole parcel only sold if no one will bid on a partial interest.',
      keyRules: [
        'At auction, the smallest undivided portion of the property (not less than 1%) is sold to cover taxes owed, unless no bidder will take a partial share, in which case the whole parcel sells',
        'Winning bidder receives a deed immediately, subject to the former owner\'s 1-year right of redemption',
        'Redemption costs: a 10% penalty if redeemed within 6 months, plus an additional 1% for each month after that, plus 1%/month interest on subsequent taxes paid by the purchaser',
        'After the 1-year redemption period expires, the purchaser must separately petition the court to foreclose the right of redemption to get full, final title',
        'Tax sales are run by individual city/town tax collectors - roughly 39 municipalities hold their own annual sales statewide',
      ],
      targetCounties: [
        { county: 'Providence', notes: 'Largest city, runs online sales via CivicSource' },
        { county: 'Warwick', notes: 'Second largest city in the state' },
        { county: 'Cranston', notes: 'Providence metro area' },
        { county: 'Newport', notes: 'Smaller market, higher property values (coastal/tourism)' },
      ],
      apnSearch: [
        { label: 'Rhode Island Division of Municipal Finance', url: 'https://dmf.ri.gov' },
        { label: 'Providence Tax Collector', url: 'https://www.providenceri.gov/collector' },
      ],
      auctionLinks: [
        { label: 'City of Providence Tax Sale Info', url: 'https://www.providenceri.gov/collector/tax-sale-information' },
      ],
      warnings: [
        'Winning a fractional/undivided interest in a property is very different from owning it outright - confirm exactly what percentage is being sold before bidding',
        'Getting full, final title requires a separate court foreclosure petition after the 1-year redemption period - this is not automatic',
        'Even after 1 year, the redemption right can sometimes be foreclosed sooner due to abandonment provisions, or extended due to other procedural issues - confirm current status with the municipality before assuming a fixed timeline',
      ],
    },
  },
];

const allStates = ['All States', ...Array.from(new Set(stateData.map(d => d.state))).sort()];
const allTypes = ['All Types', 'Tax Deed', 'Tax Lien'];
const allRatings = ['All Ratings', 'A', 'B', 'C'];

// Map of state name -> set of sale types present, used to color the map by Deed / Lien / Both / Redeemable Deed
const stateTypeMap = stateData.reduce((acc, item) => {
  if (!acc[item.state]) acc[item.state] = new Set();
  acc[item.state].add(item.type);
  return acc;
}, {});

// States where research confirmed the buyer receives the deed/title at the sale itself,
// subject to a redemption right (as opposed to standard deed states where no deed issues
// until after redemption already expired, or there's no redemption at all).
const redeemableDeedStates = new Set(
  stateData.filter(item => item.redeemableDeed).map(item => item.state)
);

// Resolve each state to a single category: 'Tax Deed', 'Tax Lien', 'Both', or 'Redeemable Deed'
const stateCategoryMap = Object.fromEntries(
  Object.entries(stateTypeMap).map(([state, types]) => {
    if (types.size > 1) return [state, 'Both'];
    const onlyType = Array.from(types)[0];
    if (onlyType === 'Tax Deed' && redeemableDeedStates.has(state)) {
      return [state, 'Redeemable Deed'];
    }
    return [state, onlyType];
  })
);

const mapTypeColors = {
  'Tax Deed': '#2563eb',
  'Tax Lien': '#16a34a',
  'Both': '#9333ea',
  'Redeemable Deed': '#ea580c',
};

function RatingBadge({ rating }) {
  const colors = { A: '#16a34a', B: '#d97706', C: '#dc2626' };
  return (
    <span style={{
      backgroundColor: colors[rating] || '#888',
      color: 'white', padding: '2px 8px',
      borderRadius: '4px', fontSize: '11px', fontWeight: 'bold'
    }}>{rating}</span>
  );
}

function TypeBadge({ type }) {
  return (
    <span style={{
      backgroundColor: type === 'Tax Deed' ? '#1a1a2e' : '#2d6a4f',
      color: 'white', padding: '2px 8px',
      borderRadius: '4px', fontSize: '11px'
    }}>{type}</span>
  );
}

function DocLinks({ state }) {
  const docs = stateDocs[state];
  if (!docs || docs.length === 0) return null;
  return (
    <div style={{ marginBottom: '20px' }}>
      <h3 style={{
        fontSize: '14px', color: '#1a1a2e', marginBottom: '10px',
        paddingBottom: '6px', borderBottom: '2px solid #c9a84c'
      }}>
        📄 Reference Documents
      </h3>
      {docs.map((doc, i) => (
        <a
          key={i}
          href={doc.url}
          target="_blank"
          rel="noreferrer"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 14px', backgroundColor: '#fffbeb',
            border: '1px solid #fde68a', borderRadius: '8px',
            marginBottom: '8px', textDecoration: 'none', color: '#1a1a2e',
            transition: 'background 0.2s'
          }}
          onMouseEnter={e => e.currentTarget.style.backgroundColor = '#fef3c7'}
          onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fffbeb'}
        >
          <div>
            <p style={{ margin: 0, fontWeight: 'bold', fontSize: '13px' }}>{doc.title}</p>
            <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#92400e' }}>{doc.type}</p>
          </div>
          <span style={{ fontSize: '18px' }}>↗</span>
        </a>
      ))}
    </div>
  );
}

function StateDetailPanel({ item, onClose }) {
  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 999
        }}
      />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: '100%', maxWidth: '500px',
        backgroundColor: 'white', zIndex: 1000,
        overflowY: 'auto', boxShadow: '-4px 0 20px rgba(0,0,0,0.2)'
      }}>
        {/* Header */}
        <div style={{
          backgroundColor: '#1a1a2e', padding: '16px',
          position: 'sticky', top: 0, zIndex: 1
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ margin: 0, color: 'white', fontSize: '18px' }}>{item.state}</h2>
              <p style={{ margin: '2px 0 0', color: '#c9a84c', fontSize: '12px' }}>
                {item.type} · {item.rating} Rated
              </p>
            </div>
            <button
              onClick={onClose}
              style={{
                backgroundColor: 'rgba(255,255,255,0.1)', border: 'none',
                color: 'white', width: '36px', height: '36px',
                borderRadius: '50%', cursor: 'pointer', fontSize: '18px'
              }}
            >✕</button>
          </div>
        </div>

        <div style={{ padding: '16px' }}>
          {/* Overview */}
          <p style={{ fontSize: '13px', color: '#555', lineHeight: '1.6', marginBottom: '20px' }}>
            {item.details.overview}
          </p>

          {/* Google Drive Docs */}
          <DocLinks state={item.state} />

          {/* Key Rules */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '14px', color: '#1a1a2e', marginBottom: '10px', paddingBottom: '6px', borderBottom: '2px solid #1a1a2e' }}>
              Key Rules
            </h3>
            {item.details.keyRules.map((rule, i) => (
              <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <span style={{ color: '#c9a84c', fontWeight: 'bold', flexShrink: 0 }}>›</span>
                <p style={{ margin: 0, fontSize: '13px', color: '#333' }}>{rule}</p>
              </div>
            ))}
          </div>

          {/* Target Counties */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '14px', color: '#1a1a2e', marginBottom: '10px', paddingBottom: '6px', borderBottom: '2px solid #1a1a2e' }}>
              Target Counties
            </h3>
            {item.details.targetCounties.map((county, i) => (
              <div key={i} style={{
                padding: '10px 12px', backgroundColor: '#f8f8f8',
                borderRadius: '6px', marginBottom: '8px',
                borderLeft: '3px solid #c9a84c'
              }}>
                <p style={{ margin: '0 0 2px', fontSize: '13px', fontWeight: 'bold', color: '#1a1a2e' }}>{county.county}</p>
                <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>{county.notes}</p>
              </div>
            ))}
          </div>

          {/* APN Search */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '14px', color: '#1a1a2e', marginBottom: '10px', paddingBottom: '6px', borderBottom: '2px solid #1a1a2e' }}>
              APN & Property Search
            </h3>
            {item.details.apnSearch.map((link, i) => (
              <a key={i} href={link.url} target="_blank" rel="noreferrer" style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 12px', backgroundColor: '#f0f8ff',
                borderRadius: '6px', marginBottom: '8px',
                textDecoration: 'none', color: '#1a1a2e',
                border: '1px solid #dde8f5', fontSize: '13px'
              }}>
                {link.label} <span>↗</span>
              </a>
            ))}
          </div>

          {/* Auction Links */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '14px', color: '#1a1a2e', marginBottom: '10px', paddingBottom: '6px', borderBottom: '2px solid #1a1a2e' }}>
              Auction Links
            </h3>
            {item.details.auctionLinks.map((link, i) => (
              <a key={i} href={link.url} target="_blank" rel="noreferrer" style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '10px 12px', backgroundColor: '#f0fff4',
                borderRadius: '6px', marginBottom: '8px',
                textDecoration: 'none', color: '#1a1a2e',
                border: '1px solid #bbf7d0', fontSize: '13px'
              }}>
                {link.label} <span>↗</span>
              </a>
            ))}
          </div>

          {/* Warnings */}
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ fontSize: '14px', color: '#dc2626', marginBottom: '10px', paddingBottom: '6px', borderBottom: '2px solid #dc2626' }}>
              ⚠️ Warnings
            </h3>
            {item.details.warnings.map((w, i) => (
              <div key={i} style={{
                padding: '10px 12px', backgroundColor: '#fff5f5',
                borderRadius: '6px', marginBottom: '8px',
                borderLeft: '3px solid #dc2626'
              }}>
                <p style={{ margin: 0, fontSize: '13px', color: '#7f1d1d' }}>{w}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function StateMap({ onSelectState }) {
  const [hovered, setHovered] = useState(null);

  return (
    <div style={{
      backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '8px',
      padding: '14px', marginBottom: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.06)'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px', flexWrap: 'wrap', gap: '6px' }}>
        <h3 style={{ margin: 0, fontSize: '14px', color: '#1a1a2e' }}>Click a highlighted state</h3>
        <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: '#666', flexWrap: 'wrap' }}>
          <span><span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '2px', backgroundColor: mapTypeColors['Tax Deed'], marginRight: '4px' }} />Tax Deed</span>
          <span><span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '2px', backgroundColor: mapTypeColors['Tax Lien'], marginRight: '4px' }} />Tax Lien</span>
          <span><span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '2px', backgroundColor: mapTypeColors['Both'], marginRight: '4px' }} />Both</span>
          <span><span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '2px', backgroundColor: mapTypeColors['Redeemable Deed'], marginRight: '4px' }} />Redeemable Deed</span>
          <span><span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '2px', backgroundColor: '#e0e0e0', marginRight: '4px' }} />No data yet</span>
        </div>
      </div>

      <ComposableMap projection="geoAlbersUsa" style={{ width: '100%', height: 'auto' }}>
        <Geographies geography={geoUrl}>
          {({ geographies }) =>
            geographies.map(geo => {
              const stateName = geo.properties.name;
              const category = stateCategoryMap[stateName];
              const hasData = Boolean(category);
              const fill = hasData ? (mapTypeColors[category] || '#888') : '#e0e0e0';

              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  onMouseEnter={() => hasData && setHovered(stateName)}
                  onMouseLeave={() => setHovered(null)}
                  onClick={() => hasData && onSelectState(stateName)}
                  style={{
                    default: {
                      fill,
                      stroke: '#fff',
                      strokeWidth: 0.75,
                      outline: 'none',
                      opacity: hasData ? 1 : 0.6,
                      cursor: hasData ? 'pointer' : 'default',
                    },
                    hover: {
                      fill: hasData ? fill : '#e0e0e0',
                      stroke: '#fff',
                      strokeWidth: 0.75,
                      outline: 'none',
                      opacity: hasData ? 0.8 : 0.6,
                      cursor: hasData ? 'pointer' : 'default',
                    },
                    pressed: {
                      fill,
                      stroke: '#fff',
                      strokeWidth: 0.75,
                      outline: 'none',
                    },
                  }}
                />
              );
            })
          }
        </Geographies>
      </ComposableMap>

      {hovered && (
        <p style={{ textAlign: 'center', fontSize: '12px', color: '#1a1a2e', fontWeight: 'bold', margin: '4px 0 0' }}>
          {hovered} — click to view cards
        </p>
      )}
    </div>
  );
}

function CheatSheet() {
  const [selectedState, setSelectedState] = useState(null);
  const [filterState, setFilterState] = useState('All States');
  const [filterType, setFilterType] = useState('All Types');
  const [filterRating, setFilterRating] = useState('All Ratings');
  const [countySearch, setCountySearch] = useState('');
  const cardsRef = useRef(null);

  const filtered = stateData.filter(item => {
    const matchState = filterState === 'All States' || item.state === filterState;
    const matchType = filterType === 'All Types' || item.type === filterType;
    const matchRating = filterRating === 'All Ratings' || item.rating === filterRating;
    const matchCounty = countySearch.trim() === '' ||
      item.details.targetCounties.some(c =>
        c.county.toLowerCase().includes(countySearch.toLowerCase())
      );
    return matchState && matchType && matchRating && matchCounty;
  });

  const handleMapSelect = (stateName) => {
    setFilterState(stateName);
    if (cardsRef.current) {
      cardsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const selectStyle = {
    padding: '8px 12px', borderRadius: '6px',
    border: '1px solid #ddd', backgroundColor: 'white',
    fontSize: '13px', color: '#1a1a2e', cursor: 'pointer',
    outline: 'none'
  };

  return (
    <div style={{ padding: '0 0 40px' }}>
      <h2 style={{ marginBottom: '6px', fontSize: '20px', color: '#1a1a2e' }}>
        State & County Resource Center
      </h2>
      <p style={{ margin: '0 0 20px', color: '#666', fontSize: '13px' }}>
        Browse tax deed and lien data by state, type, or county. Click any card for detailed rules, links, and reference documents.
      </p>

      {/* Map */}
      <StateMap onSelectState={handleMapSelect} />

      {/* Filters */}
      <div ref={cardsRef} style={{
        display: 'flex', flexWrap: 'wrap', gap: '10px',
        marginBottom: '20px', alignItems: 'center'
      }}>
        <select value={filterState} onChange={e => setFilterState(e.target.value)} style={selectStyle}>
          {allStates.map(s => <option key={s}>{s}</option>)}
        </select>

        <select value={filterType} onChange={e => setFilterType(e.target.value)} style={selectStyle}>
          {allTypes.map(t => <option key={t}>{t}</option>)}
        </select>

        <select value={filterRating} onChange={e => setFilterRating(e.target.value)} style={selectStyle}>
          {allRatings.map(r => <option key={r}>{r}</option>)}
        </select>

        <input
          type="text"
          placeholder="Search by county..."
          value={countySearch}
          onChange={e => setCountySearch(e.target.value)}
          style={{
            ...selectStyle,
            flexGrow: 1, minWidth: '160px',
            cursor: 'text'
          }}
        />

        {(filterState !== 'All States' || filterType !== 'All Types' || filterRating !== 'All Ratings' || countySearch) && (
          <button
            onClick={() => { setFilterState('All States'); setFilterType('All Types'); setFilterRating('All Ratings'); setCountySearch(''); }}
            style={{
              padding: '8px 14px', borderRadius: '6px',
              border: '1px solid #ddd', backgroundColor: '#f5f5f5',
              fontSize: '12px', cursor: 'pointer', color: '#666'
            }}
          >Clear Filters</button>
        )}
      </div>

      {/* Results count */}
      <p style={{ fontSize: '12px', color: '#999', marginBottom: '14px' }}>
        Showing {filtered.length} of {stateData.length} entries
      </p>

      {/* Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
        gap: '12px'
      }}>
        {filtered.map((item, index) => (
          <div key={index} style={{
            border: '1px solid #ddd', borderRadius: '8px',
            padding: '14px', backgroundColor: '#fff',
            boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
            display: 'flex', flexDirection: 'column'
          }}>
            {/* Card Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h3 style={{ margin: 0, fontSize: '15px', color: '#1a1a2e' }}>{item.state}</h3>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <TypeBadge type={item.type} />
                <RatingBadge rating={item.rating} />
              </div>
            </div>

            {/* Stats */}
            <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse', marginBottom: '10px' }}>
              <tbody>
                {[
                  ['Penalty/Return', item.penalty],
                  ['Redemption', item.redemption],
                  ['Schedule', item.schedule],
                ].map(([label, value]) => (
                  <tr key={label}>
                    <td style={{ color: '#888', padding: '3px 0', width: '42%' }}>{label}</td>
                    <td style={{ color: '#333' }}>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p style={{ margin: '0 0 12px', fontSize: '11px', color: '#555', borderTop: '1px solid #eee', paddingTop: '8px', flexGrow: 1 }}>
              {item.notes}
            </p>

            {/* Doc indicator */}
            {stateDocs[item.state] && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                fontSize: '11px', color: '#92400e', backgroundColor: '#fffbeb',
                padding: '5px 8px', borderRadius: '4px', marginBottom: '8px',
                border: '1px solid #fde68a'
              }}>
                📄 {stateDocs[item.state].length} reference doc{stateDocs[item.state].length > 1 ? 's' : ''} available
              </div>
            )}

            <button
              onClick={() => setSelectedState(item)}
              style={{
                width: '100%', padding: '8px',
                backgroundColor: '#1a1a2e', color: 'white',
                border: 'none', borderRadius: '6px',
                cursor: 'pointer', fontSize: '13px', fontWeight: 'bold'
              }}
            >
              View Details & Resources
            </button>
          </div>
        ))}

        {filtered.length === 0 && (
          <div style={{
            gridColumn: '1/-1', textAlign: 'center',
            padding: '40px', color: '#999', fontSize: '14px'
          }}>
            No results match your filters. <button onClick={() => { setFilterState('All States'); setFilterType('All Types'); setFilterRating('All Ratings'); setCountySearch(''); }} style={{ background: 'none', border: 'none', color: '#c9a84c', cursor: 'pointer', fontWeight: 'bold' }}>Clear all</button>
          </div>
        )}
      </div>

      {/* Detail Panel */}
      {selectedState && (
        <StateDetailPanel item={selectedState} onClose={() => setSelectedState(null)} />
      )}
    </div>
  );
}

export default CheatSheet;
